import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, Renderer2, SecurityContext, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormlyModule } from '@ngx-formly/core';
import { TranslateModule } from '@ngx-translate/core';
import { IonicModule, ModalController } from '@ionic/angular';
import { IActionMapping, ITreeOptions, ITreeState, TreeModule, TreeNode } from '@ali-hm/angular-tree-component';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { AuthService } from '@app/core/services/auth.service';
import { ContextService } from '@app/core/services/context.service';
import { MediaService } from '@app/core/services/media.service';
import { MiscService } from '@app/core/services/misc.service';
import { Reminder, ReminderHostType } from '@app/core/models/reminder.model';
import { SettingsService } from '@app/core/services/settings.service';
import { ExpandableTextDirective } from '@app/core/directives/ExpandableText/expandable-text.directive';
import { TooltipDirective } from '@app/core/directives/ToolTips/tooltip.directive';
import { HttpImgSrcPipe } from '@app/pipes/http-img-src.pipe';
import { ClaimDocumentRemindersComponent } from '@app/shared/components/claim-document-reminders/claim-document-reminders.component';
import { FormlyFieldAnyComponent } from '@app/shared/components/formly-components/formly-field-any/formly-field-any.component';
import { DynamicIconComponent } from "@app/shared/components/dynamic-icon";
import { v4 as uuidv4 } from 'uuid';
import { environment } from '@env/environment';
import { CarouselModalComponent } from '@app/shared/components/carousel-modal/carousel-modal.component';
import { EmailViewerComponent } from '@app/shared/components/email-viewer/email-viewer.component';
import { PdfViewerComponent } from '@app/shared/components/pdf-viewer/pdf-viewer.component';
import { Medium } from '@app/shared/components/formly-components/formly-field-picture/formly-field-picture.component';
import { IncomingMessagesService } from '@app/core/services/incoming-messages.service';
import { LanguageService } from '@app/core/services/language.service';
import {
  MailDropPayload,
  buildEmlFromIncomingMessage,
  buildFilesFromMailDropPayload,
  buildMailDropFileName,
  buildMessageIdLookupCandidates,
  fileContainsMailDropFallback,
  isEmlContentIncomplete,
  mailDropPayloadWouldUseBodyFallback,
  mergeMailDropStringMaps,
  resolveMailDropPayload,
  snapshotMailDropStrings,
  startMailDropStringItemReads,
} from '@app/core/utils/mail-eml.util';

export interface DocumentFolderNode {
  id: string;
  text: string;
  label?: string;
  children?: DocumentFolderNode[];
}

export interface DocumentMedia {
  fileId: string;
  originalname: string;
  mediaType: string;
}

export interface DocumentItem {
  id: string;
  folderCategory: string;
  folderType: string;
  folderUuid: string;
  folderName: string;
  userId: string;
  userFriendlyName: string; 
  documentName: string;
  documentDescription: string;
  documentEntryDate: string;
  documentDate: string;
  appendix: boolean;
  media: DocumentMedia[];
  selectedFiles: File[];
  isDeleted?: boolean;
}

type DocumentViewMode = 'chronology' | 'folders';

type DocumentPropertyMap = {
  id: string;
  folderCategory: string;
  folderType: string;
  folderUuid: string;
  folderName: string;
  userId: string;
  userFriendlyName: string;
  documentName: string;
  documentDescription: string;
  documentEntryDate: string;
  documentDate: string;
  appendix: string;
  media: string;
  selectedFiles: string;
};

interface InternalDocumentItem extends DocumentItem {
  __source: Record<string, unknown>;
  isNew?: boolean;
}

interface DocumentAttachmentPreview {
  id: string;
  name: string;
  mediaType: string;
  isExisting: boolean;
  fileId?: string;
  file?: File;
}

interface FolderTypeOption {
  categoryId: string;
  typeId: string;
  categoryText: string;
  typeText: string;
}

interface DocumentCategoryOption {
  id: string;
  text: string;
}

interface DocumentTypeOption {
  id: string;
  text: string;
  categoryId: string;
}

interface DocumentFolderNameGroup {
  folderName: string;
  documentCount: number;
}

type DocumentDropKind = 'document' | 'category' | 'folderType' | 'folderName' | 'newDocument';

interface DocumentDropContext {
  kind: DocumentDropKind;
  categoryId?: string;
  typeId?: string;
  folderName?: string;
  documentId?: string;
}

type FolderPickerScope = 'newDocument' | 'dropCreate' | 'detail';

interface FolderPickerState {
  nodes: DocumentTreeNode[];
  treeState: ITreeState;
  selectedNodeId: string | null;
  collapsed: boolean;
  lockedCategoryId: string;
}

interface DropCreateDraft {
  folderCategory: string;
  folderType: string;
  folderName: string;
  documentName: string;
  documentDescription: string;
  documentDate: string;
  appendix: boolean;
  files: File[];
}

interface DocumentTreeNode {
  id: string;
  name: string;
  categoryId?: string;
  typeId?: string;
  children?: DocumentTreeNode[];
}

const DEFAULT_PROPERTY_MAP: DocumentPropertyMap = {
  id: 'id',
  folderCategory: 'folderCategory',
  folderType: 'folderType',
  folderUuid: 'folderUuid',
  folderName: 'folderName',
  userId: 'userId',
  userFriendlyName: 'userFriendlyName',
  documentName: 'documentName',
  documentDescription: 'documentDescription',
  documentEntryDate: 'documentEntryDate',
  documentDate: 'documentDate',
  appendix: 'appendix',
  media: 'media',
  selectedFiles: 'selectedFiles',
};

const CATEGORY_BUBBLE_CLASS: Record<string, string> = {
  constitution: 'category-constitution',
  formalNotice: 'category-formal-notice',
  brokerInsurer: 'category-broker-insurer',
  experts: 'category-experts',
  works: 'category-works',
  payments: 'category-payments',
  litigation: 'category-litigation',
  others: 'category-others',
};

@Component({
  selector: 'app-formly-field-documents',
  templateUrl: './formly-field-documents.component.html',
  styleUrls: ['./formly-field-documents.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonicModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FormlyModule,
    TranslateModule,
    TreeModule,
    ExpandableTextDirective,
    TooltipDirective,
    HttpImgSrcPipe,
    DynamicIconComponent,
    ClaimDocumentRemindersComponent,
],
})
export class FormlyFieldDocumentsComponent extends FormlyFieldAnyComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('folderTreeLabel', { read: ElementRef })
  public folderTreeLabel?: ElementRef<HTMLElement>;
  @ViewChild('detailModalDialog', { read: ElementRef })
  public detailModalDialog?: ElementRef<HTMLElement>;
  @ViewChild('discardConfirmDialog', { read: ElementRef })
  public discardConfirmDialog?: ElementRef<HTMLElement>;
  public folderTree: DocumentFolderNode[] = [];
  public categoryOptions: DocumentCategoryOption[] = [];
  public typeOptions: DocumentTypeOption[] = [];
  public typeOptionsByCategory: Record<string, DocumentTypeOption[]> = {};
  public folderTypeOptions: FolderTypeOption[] = [];
  public folderTreeNodes: DocumentTreeNode[] = [];
  public folderTreeState: ITreeState = {};
  public readonly folderTreeRootId = '__folder_root__';
  public isFolderTreeCollapsed = true;
  public folderTreeOptions: ITreeOptions = {
    allowDrag: () => false,
    allowDrop: () => false,
    actionMapping: this.getFolderTreeActionMapping(),
    idField: 'id',
    displayField: 'name',
    childrenField: 'children',
    nodeHeight: 23,
    levelPadding: 16,
    animateExpand: true,
    animateSpeed: 30,
    animateAcceleration: 1.2,
  };
  public selectedFolderNodeId: string | null = null;

  public viewMode: DocumentViewMode = 'chronology';
  public searchText = '';
  public showChronologyFilters = false;
  public chronologyFromDate = '';
  public chronologyToDate = '';
  public chronologyCategoryFilter = '';
  public chronologyTypeFilter = '';

  public documents: InternalDocumentItem[] = [];
  public chronologyDocuments: InternalDocumentItem[] = [];

  public newDocumentCategory = '';
  public newDocumentType = '';
  public newDocumentFolderSelection = '';
  public newDocumentName = '';
  public newDocumentDescription = '';
  public newDocumentDate = this.getTodayDateInputValue();
  public newDocumentAppendix = false;
  public newDocumentFolderName = '';
  public newDocumentSelectedFiles: File[] = [];
  public newDocumentFormInteracted = false;
  private newDocumentSubmitAttempted = false;

  public selectedDocument: InternalDocumentItem | null = null;
  public selectedDocumentDraft: InternalDocumentItem | null = null;
  public isDetailModalOpen = false;
  public isDiscardConfirmOpen = false;
  public isDropCreateModalOpen = false;
  public dropCreateDraft: DropCreateDraft | null = null;
  public dropCreateContext: DocumentDropContext | null = null;
  public activeDropTargetKey: string | null = null;
  private dropProcessing = false;
  public readonly dropCreateFolderTreeOptions: ITreeOptions;
  public readonly detailFolderTreeOptions: ITreeOptions;
  public readonly folderPickers: Record<FolderPickerScope, FolderPickerState>;

  public folderTypeVisibleCount: Record<string, number> = {};
  public folderDocumentVisibleCount: Record<string, number> = {};
  public reminders: Reminder[] = [];

  private readonly destroy$ = new Subject<void>();
  private readonly defaultSettingKey = 'doc_categ_tree.claims';
  private readonly localImagePreviewUrls = new Map<string, string>();
  private readonly localFilePreviewUrls = new Map<File, string>();
  private readonly localFileIds = new Map<File, string>();
  private readonly brokenImagePath = 'assets/imgs/broken_image.svg';
  private readonly overlayPositionListener = () => this.updateFolderTreeOverlayPosition();
  private restoreFocusTarget: HTMLElement | null = null;

  constructor(
    private readonly settingsService: SettingsService,
    private readonly contextService: ContextService,
    private readonly authService: AuthService,
    private readonly mediaService: MediaService,
    private readonly miscService: MiscService,
    private readonly cdr: ChangeDetectorRef,
    private readonly hostRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly modalController: ModalController,
    private readonly ngZone: NgZone,
    private readonly domSanitizer: DomSanitizer,
    private readonly incomingMessagesService: IncomingMessagesService,
    private readonly languageService: LanguageService,
  ) {
    super();
    this.folderPickers = {
      newDocument: this.createFolderPickerState(),
      dropCreate: this.createFolderPickerState(),
      detail: this.createFolderPickerState(),
    };
    this.dropCreateFolderTreeOptions = this.createFolderPickerOptions('dropCreate');
    this.detailFolderTreeOptions = this.createFolderPickerOptions('detail');
  }

  ngOnInit(): void {
    this.viewMode = this.getDefaultViewMode();
    this.subscribeToModelChanges();
    this.loadFolderTree();
    this.updateFromControlValue(this.formControl.value);
  }

  ngAfterViewInit(): void {
    this.updateFolderTreeOverlayPosition();
    window.addEventListener('scroll', this.overlayPositionListener, true);
    window.addEventListener('resize', this.overlayPositionListener, true);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.revokeAllLocalMediaPreviews();
    window.removeEventListener('scroll', this.overlayPositionListener, true);
    window.removeEventListener('resize', this.overlayPositionListener, true);
  }

  public get canEdit(): boolean {
    return !(this.options.formState.disabled || this.field.props?.['disabled'] || this.field.props?.['readonly']);
  }

  public get visibleFolderTypeCount(): number {
    return this.getMaxVisibleFolderTypes();
  }

  public get hasActiveChronologyFilters(): boolean {
    return [
      this.chronologyFromDate,
      this.chronologyToDate,
      this.chronologyCategoryFilter,
      this.chronologyTypeFilter,
    ].some((value) => (value || '').trim().length > 0);
  }

  public setViewMode(mode: DocumentViewMode): void {
    this.viewMode = mode;
    if (mode !== 'chronology') {
      this.showChronologyFilters = false;
    }
    this.cdr.markForCheck();
  }

  public toggleChronologyFilters(): void {
    this.showChronologyFilters = !this.showChronologyFilters;
    this.cdr.markForCheck();
  }

  public onSearchChange(value: string): void {
    this.searchText = value;
    this.computeChronologyDocuments();
    this.cdr.markForCheck();
  }

  public onChronologyFilterChange(): void {
    this.computeChronologyDocuments();
    this.cdr.markForCheck();
  }

  public getTypesForCategory(categoryId: string): DocumentTypeOption[] {
    return this.typeOptionsByCategory[categoryId] || [];
  }

  public getVisibleCategoryChildren(category: DocumentFolderNode): DocumentFolderNode[] {
    const nonEmptyChildren = this.getNonEmptyCategoryChildren(category);
    const max = this.folderTypeVisibleCount[category.id] || this.visibleFolderTypeCount;
    return nonEmptyChildren.slice(0, max);
  }

  public canShowMoreCategoryChildren(category: DocumentFolderNode): boolean {
    const children = this.getNonEmptyCategoryChildren(category);
    const max = this.folderTypeVisibleCount[category.id] || this.visibleFolderTypeCount;
    return children.length > max;
  }

  public getNonEmptyCategoryChildren(category: DocumentFolderNode): DocumentFolderNode[] {
    return (category.children || []).filter((typeNode) => this.getDocumentsForFolder(category.id, typeNode.id).length > 0);
  }

  public hasVisibleFolders(): boolean {
    return this.folderTree.some((category) => this.getNonEmptyCategoryChildren(category).length > 0);
  }

  public showMoreCategoryChildren(categoryId: string): void {
    this.folderTypeVisibleCount[categoryId] = (this.folderTypeVisibleCount[categoryId] || this.visibleFolderTypeCount) + this.visibleFolderTypeCount;
    this.cdr.markForCheck();
  }

  public getDocumentsForFolder(categoryId: string, typeId: string): InternalDocumentItem[] {
    const filtered = this.documents.filter((doc) =>
      this.isDocumentVisible(doc)
      && doc.folderCategory === categoryId
      && doc.folderType === typeId
      && this.matchesSearch(doc)
    );

    filtered.sort((a, b) => this.getDateMs(b.documentEntryDate) - this.getDateMs(a.documentEntryDate));
    return filtered;
  }

  public getFolderNameGroupsForType(categoryId: string, typeId: string): DocumentFolderNameGroup[] {
    const docs = this.getDocumentsForFolder(categoryId, typeId);
    const countsByFolderName = new Map<string, number>();

    for (const doc of docs) {
      const folderName = doc.folderName?.trim() || '';
      countsByFolderName.set(folderName, (countsByFolderName.get(folderName) || 0) + 1);
    }

    return Array.from(countsByFolderName.entries())
      .sort(([left], [right]) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
      .map(([folderName, documentCount]) => ({ folderName, documentCount }));
  }

  public shouldShowFolderNameLayer(folderGroup: DocumentFolderNameGroup): boolean {
    return folderGroup.documentCount > 1;
  }

  public getDocumentsForFolderName(categoryId: string, typeId: string, folderName: string): InternalDocumentItem[] {
    return this.getDocumentsForFolder(categoryId, typeId)
      .filter((doc) => (doc.folderName?.trim() || '') === folderName);
  }

  public getVisibleDocumentsForFolderName(categoryId: string, typeId: string, folderName: string): InternalDocumentItem[] {
    const docs = this.getDocumentsForFolderName(categoryId, typeId, folderName);
    const key = this.getFolderNameKey(categoryId, typeId, folderName);
    const max = this.folderDocumentVisibleCount[key] || this.getMaxVisibleDocumentsPerFolder();
    return docs.slice(0, max);
  }

  public canShowMoreDocumentsForFolderName(categoryId: string, typeId: string, folderName: string): boolean {
    const docs = this.getDocumentsForFolderName(categoryId, typeId, folderName);
    const key = this.getFolderNameKey(categoryId, typeId, folderName);
    const max = this.folderDocumentVisibleCount[key] || this.getMaxVisibleDocumentsPerFolder();
    return docs.length > max;
  }

  public showMoreDocumentsForFolderName(categoryId: string, typeId: string, folderName: string): void {
    const key = this.getFolderNameKey(categoryId, typeId, folderName);
    this.folderDocumentVisibleCount[key] = (this.folderDocumentVisibleCount[key] || this.getMaxVisibleDocumentsPerFolder()) + this.getMaxVisibleDocumentsPerFolder();
    this.cdr.markForCheck();
  }

  public onNewDocumentCategoryChange(): void {
    if (!this.getTypesForCategory(this.newDocumentCategory).some((item) => item.id === this.newDocumentType)) {
      this.newDocumentType = '';
    }
    this.newDocumentFolderSelection = this.buildFolderSelectionValue(this.newDocumentCategory, this.newDocumentType);
    this.selectedFolderNodeId = this.newDocumentFolderSelection || null;
    this.folderTreeState.selectedNodeIds = this.selectedFolderNodeId
      ? { [this.selectedFolderNodeId]: true }
      : {};
    this.isFolderTreeCollapsed = this.hasValidFolderSelection();
    this.cdr.markForCheck();
  }

  public onNewDocumentFolderSelectionChange(value: string): void {
    this.newDocumentFormInteracted = true;
    const [categoryId, typeId] = this.parseFolderSelection(value);
    this.newDocumentCategory = categoryId;
    this.newDocumentType = typeId;
    this.newDocumentFolderSelection = this.buildFolderSelectionValue(categoryId, typeId);
    this.selectedFolderNodeId = this.newDocumentFolderSelection || null;
    this.folderTreeState.selectedNodeIds = this.selectedFolderNodeId
      ? { [this.selectedFolderNodeId]: true }
      : {};
    this.isFolderTreeCollapsed = this.hasValidFolderSelection();
    this.cdr.markForCheck();
  }

  public expandFolderTree(): void {
    this.newDocumentFormInteracted = true;
    this.isFolderTreeCollapsed = false;
    this.ensureFolderTreeExpanded();
    this.scheduleFolderTreeOverlayPosition();
    this.cdr.markForCheck();
  }

  public getSelectedFolderLabel(): string {
    if (!this.hasValidFolderSelection()) {
      return '';
    }
    return `${this.getCategoryText(this.newDocumentCategory)} ➢ ${this.getTypeText(this.newDocumentCategory, this.newDocumentType)}`;
  }

  public hasFolderSelection(): boolean {
    return this.newDocumentCategory.trim().length > 0 && this.newDocumentType.trim().length > 0;
  }

  public hasValidFolderSelection(): boolean {
    if (!this.hasFolderSelection()) {
      return false;
    }

    return this.getTypesForCategory(this.newDocumentCategory)
      .some((item) => item.id === this.newDocumentType);
  }

  public onFolderTreeNodeClick(node: TreeNode): void {
    this.onFolderPickerNodeClick(node, 'newDocument');
  }

  public shouldShowFolderPickerInDropModal(): boolean {
    return this.dropCreateContext?.kind !== 'folderName';
  }

  public getDropCreateLockedCategoryId(): string {
    const kind = this.dropCreateContext?.kind;
    if (kind === 'category' || kind === 'folderType' || kind === 'folderName') {
      return this.dropCreateContext?.categoryId || '';
    }
    return '';
  }

  public getFolderPickerNodes(scope: FolderPickerScope): DocumentTreeNode[] {
    return this.folderPickers[scope].nodes;
  }

  public isFolderPickerCollapsed(scope: FolderPickerScope): boolean {
    return this.folderPickers[scope].collapsed;
  }

  public expandFolderPicker(scope: FolderPickerScope): void {
    this.folderPickers[scope].collapsed = false;
    this.ensureFolderPickerExpanded(scope);
    this.cdr.markForCheck();
  }

  public getFolderPickerLabel(categoryId: string, typeId: string): string {
    if (!categoryId || !typeId) {
      return '';
    }
    return `${this.getCategoryText(categoryId)} ➢ ${this.getTypeText(categoryId, typeId)}`;
  }

  public hasValidFolderPickerSelection(categoryId: string, typeId: string): boolean {
    if (!categoryId.trim() || !typeId.trim()) {
      return false;
    }

    return this.getTypesForCategory(categoryId).some((item) => item.id === typeId);
  }

  public isNewDocumentFolderSelectionInvalid(): boolean {
    return !this.hasValidFolderSelection();
  }

  public isNewDocumentNameInvalid(): boolean {
    return !this.newDocumentName.trim();
  }

  public isNewDocumentDateInvalid(): boolean {
    return !this.newDocumentDate.trim();
  }

  public isNewDocumentFolderNameInvalid(): boolean {
    return !this.newDocumentFolderName.trim();
  }

  public shouldShowNewDocumentFieldError(isInvalid: boolean): boolean {
    if (!isInvalid) {
      return false;
    }

    if (this.newDocumentSubmitAttempted) {
      return true;
    }

    return this.newDocumentFormInteracted && this.hasNewDocumentPartialProgress();
  }

  public onNewDocumentFormFieldChange(): void {
    this.newDocumentFormInteracted = true;
    this.onFormFieldChange();
  }

  private hasNewDocumentPartialProgress(): boolean {
    return this.hasValidFolderSelection()
      || this.newDocumentFolderName.trim().length > 0
      || this.newDocumentName.trim().length > 0;
  }

  public isDetailFolderSelectionInvalid(): boolean {
    if (!this.selectedDocumentDraft) {
      return false;
    }

    return !this.hasValidFolderPickerSelection(
      this.selectedDocumentDraft.folderCategory,
      this.selectedDocumentDraft.folderType,
    );
  }

  public isDetailDocumentNameInvalid(): boolean {
    return !this.selectedDocumentDraft?.documentName.trim();
  }

  public isDetailDocumentDateInvalid(): boolean {
    return !this.selectedDocumentDraft?.documentDate.trim();
  }

  public isDetailFolderNameInvalid(): boolean {
    return !this.selectedDocumentDraft?.folderName.trim();
  }

  public isDropCreateFolderSelectionInvalid(): boolean {
    if (!this.dropCreateDraft || !this.shouldShowFolderPickerInDropModal()) {
      return false;
    }

    return !this.hasValidFolderPickerSelection(
      this.dropCreateDraft.folderCategory,
      this.dropCreateDraft.folderType,
    );
  }

  public isDropCreateDocumentNameInvalid(): boolean {
    return !this.dropCreateDraft?.documentName.trim();
  }

  public isDropCreateDocumentDateInvalid(): boolean {
    return !this.dropCreateDraft?.documentDate.trim();
  }

  public isDropCreateFolderNameInvalid(): boolean {
    return !this.dropCreateDraft?.folderName.trim();
  }

  public isFolderPickerNodeSelected(scope: FolderPickerScope, node: TreeNode): boolean {
    return node.id === this.folderPickers[scope].selectedNodeId;
  }

  public canSelectFolderNode(node: TreeNode): boolean {
    return this.isFolderLeafNode(node);
  }

  public onFormFieldChange(): void {
    this.cdr.markForCheck();
  }

  public onFilesSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files;
    if (!files || files.length === 0) {
      return;
    }

    this.appendFilesToNewDocumentSelection(this.extractFilesFromFileList(files));
    target.value = '';
    this.cdr.markForCheck();
  }

  public onDropZoneDragOver(event: DragEvent, context: DocumentDropContext): void {
    if (!this.isPotentialFileDrag(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer!.dropEffect = 'copy';
    this.activeDropTargetKey = this.buildDropTargetKey(context);
    this.cdr.markForCheck();
  }

  public onDropZoneDragLeave(event: DragEvent, context: DocumentDropContext): void {
    if (!this.isPotentialFileDrag(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const key = this.buildDropTargetKey(context);
    if (this.activeDropTargetKey !== key) {
      return;
    }

    const currentTarget = event.currentTarget as HTMLElement | null;
    const relatedTarget = event.relatedTarget as Node | null;
    if (!currentTarget) {
      return;
    }

    // Dropping often triggers dragleave with a null relatedTarget — keep the active target.
    if (!relatedTarget) {
      return;
    }

    if (currentTarget.contains(relatedTarget)) {
      return;
    }

    this.activeDropTargetKey = null;
    this.cdr.markForCheck();
  }

  public async onDropZoneDrop(event: DragEvent, context: DocumentDropContext): Promise<void> {
    if (this.dropProcessing || !this.canEdit) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.activeDropTargetKey = null;
    await this.processDrop(event, context);
  }

  public async onHostDropCapture(event: DragEvent): Promise<void> {
    if (this.dropProcessing || !this.canEdit) {
      return;
    }

    const context = this.resolveDropContextForEvent(event);
    if (!context) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.activeDropTargetKey = null;
    await this.processDrop(event, context);
  }

  public onHostDragEndCapture(): void {
    this.activeDropTargetKey = null;
    this.cdr.markForCheck();
  }

  private async processDrop(event: DragEvent, context: DocumentDropContext): Promise<void> {
    this.dropProcessing = true;
    try {
      const { files, incompleteMailDrop } = await this.extractDroppedFiles(event);

      if (incompleteMailDrop) {
        await this.notifyIncompleteMailDrop();
      }

      if (files.length === 0) {
        this.cdr.markForCheck();
        return;
      }

      await this.handleDroppedFiles(context, files);
      this.cdr.markForCheck();
    } finally {
      this.dropProcessing = false;
    }
  }

  private resolveDropContextForEvent(event: DragEvent): DocumentDropContext | null {
    if (this.activeDropTargetKey) {
      const parsed = this.parseDropTargetKey(this.activeDropTargetKey);
      if (parsed) {
        return parsed;
      }
    }

    return this.resolveDropContextFromElement(event.target);
  }

  private resolveDropContextFromElement(target: EventTarget | null): DocumentDropContext | null {
    if (!(target instanceof HTMLElement)) {
      return null;
    }

    let current: HTMLElement | null = target;
    while (current && !current.classList.contains('document-field')) {
      const kind = current.dataset['dropKind'] as DocumentDropKind | undefined;
      if (kind) {
        return {
          kind,
          categoryId: current.dataset['dropCategoryId'],
          typeId: current.dataset['dropTypeId'],
          folderName: current.dataset['dropFolderName'],
          documentId: current.dataset['dropDocumentId'],
        };
      }

      current = current.parentElement;
    }

    return null;
  }

  private parseDropTargetKey(key: string): DocumentDropContext | null {
    if (key === 'new-document') {
      return { kind: 'newDocument' };
    }

    if (key.startsWith('doc:')) {
      return { kind: 'document', documentId: key.slice(4) };
    }

    if (key.startsWith('category:')) {
      return { kind: 'category', categoryId: key.slice('category:'.length) };
    }

    if (key.startsWith('type:')) {
      const separatorIndex = key.indexOf('::');
      if (separatorIndex === -1) {
        return null;
      }

      return {
        kind: 'folderType',
        categoryId: key.slice('type:'.length, separatorIndex),
        typeId: key.slice(separatorIndex + 2),
      };
    }

    if (key.startsWith('folder:')) {
      const remainder = key.slice('folder:'.length);
      const firstSeparator = remainder.indexOf('::');
      const secondSeparator = remainder.indexOf('::', firstSeparator + 2);
      if (firstSeparator === -1 || secondSeparator === -1) {
        return null;
      }

      return {
        kind: 'folderName',
        categoryId: remainder.slice(0, firstSeparator),
        typeId: remainder.slice(firstSeparator + 2, secondSeparator),
        folderName: remainder.slice(secondSeparator + 2),
      };
    }

    return null;
  }

  public onHostDragOverCapture(event: DragEvent): void {
    if (!this.canEdit || !this.isPotentialFileDrag(event)) {
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  public onHostDragEnterCapture(event: DragEvent): void {
    if (!this.canEdit || !this.isPotentialFileDrag(event)) {
      return;
    }

    event.preventDefault();
  }

  public isDropTargetActive(context: DocumentDropContext): boolean {
    return this.activeDropTargetKey === this.buildDropTargetKey(context);
  }

  public shouldShowDropCreateFolderNameInput(): boolean {
    if (this.dropCreateContext?.kind !== 'folderName') {
      return true;
    }

    return !this.dropCreateDraft?.folderName?.trim();
  }

  public shouldShowDropCreateFolderNameReadonly(): boolean {
    return this.dropCreateContext?.kind === 'folderName' && !!this.dropCreateDraft?.folderName?.trim();
  }

  public canConfirmDropCreate(): boolean {
    if (!this.dropCreateDraft || this.dropCreateDraft.files.length === 0) {
      return false;
    }

    const hasValidFolder = this.dropCreateDraft.folderCategory.trim().length > 0
      && this.dropCreateDraft.folderType.trim().length > 0
      && this.getTypesForCategory(this.dropCreateDraft.folderCategory)
        .some((item) => item.id === this.dropCreateDraft?.folderType);

    return hasValidFolder
      && this.dropCreateDraft.folderName.trim().length > 0
      && this.dropCreateDraft.documentName.trim().length > 0
      && this.dropCreateDraft.documentDate.trim().length > 0;
  }

  public removeDropCreateFile(index: number): void {
    if (!this.dropCreateDraft || index < 0 || index >= this.dropCreateDraft.files.length) {
      return;
    }

    const removed = this.dropCreateDraft.files[index];
    if (removed) {
      this.revokeLocalFilePreview(removed);
    }
    this.dropCreateDraft.files.splice(index, 1);
    this.dropCreateDraft.files = [...this.dropCreateDraft.files];
    this.cdr.markForCheck();
  }

  public closeDropCreateModal(): void {
    if (this.dropCreateDraft) {
      this.cleanupLocalFilePreviews(this.dropCreateDraft.files);
    }
    this.isDropCreateModalOpen = false;
    this.dropCreateDraft = null;
    this.dropCreateContext = null;
    this.cdr.markForCheck();
  }

  public confirmDropCreate(): void {
    if (!this.canEdit || !this.canConfirmDropCreate() || !this.dropCreateDraft) {
      return;
    }

    const draft = this.dropCreateDraft;
    const now = new Date().toISOString();
    const newDoc: InternalDocumentItem = {
      id: uuidv4(),
      __source: {},
      isNew: true,
      folderCategory: draft.folderCategory.trim(),
      folderType: draft.folderType.trim(),
      folderUuid: this.buildFolderUuid(),
      folderName: draft.folderName.trim(),
      userId: this.getCurrentUserId(),
      userFriendlyName: this.authService.currentUserFriendlyName,
      documentName: draft.documentName.trim(),
      documentDescription: draft.documentDescription.trim(),
      documentEntryDate: now,
      documentDate: draft.documentDate,
      appendix: draft.appendix,
      media: [],
      selectedFiles: [...draft.files],
    };

    this.documents = [newDoc, ...this.documents];
    this.persistDocuments();
    this.isDropCreateModalOpen = false;
    this.dropCreateDraft = null;
    this.dropCreateContext = null;
    this.cdr.markForCheck();
  }

  public removeNewMedia(index: number): void {
    if (index < 0 || index >= this.newDocumentSelectedFiles.length) {
      return;
    }
    const removed = this.newDocumentSelectedFiles[index];
    if (removed) {
      this.revokeLocalFilePreview(removed);
    }
    this.newDocumentSelectedFiles.splice(index, 1);
    this.newDocumentSelectedFiles = [...this.newDocumentSelectedFiles];
    this.cdr.markForCheck();
  }

  public onDetailFilesSelected(event: Event): void {
    if (!this.selectedDocumentDraft || !this.canEdit) {
      return;
    }

    const target = event.target as HTMLInputElement;
    const files = target.files;
    if (!files || files.length === 0) {
      return;
    }

    for (let i = 0; i < files.length; i += 1) {
      const file = files.item(i);
      if (!file) {
        continue;
      }

      this.registerLocalFilePreview(file);
      this.selectedDocumentDraft.selectedFiles.push(file);
    }

    this.selectedDocumentDraft.selectedFiles = [...this.selectedDocumentDraft.selectedFiles];
    target.value = '';
    this.cdr.markForCheck();
  }

  public removeDetailMedia(index: number): void {
    if (!this.selectedDocumentDraft || !this.canEdit) {
      return;
    }
    if (index < 0 || index >= this.selectedDocumentDraft.media.length) {
      return;
    }

    const removed = this.selectedDocumentDraft.media[index];
    if (removed) {
      this.revokeLocalMediaPreview(removed.fileId);
    }
    this.selectedDocumentDraft.media.splice(index, 1);
    this.selectedDocumentDraft.media = [...this.selectedDocumentDraft.media];
    this.cdr.markForCheck();
  }

  public removeDetailSelectedFile(index: number): void {
    if (!this.selectedDocumentDraft || !this.canEdit) {
      return;
    }
    if (index < 0 || index >= this.selectedDocumentDraft.selectedFiles.length) {
      return;
    }

    const removed = this.selectedDocumentDraft.selectedFiles[index];
    if (removed) {
      this.revokeLocalFilePreview(removed);
    }
    this.selectedDocumentDraft.selectedFiles.splice(index, 1);
    this.selectedDocumentDraft.selectedFiles = [...this.selectedDocumentDraft.selectedFiles];
    this.cdr.markForCheck();
  }

  public isLocalMediaFileId(fileId: string): boolean {
    return fileId.startsWith('local_');
  }

  public getMediaImageSrc(media: DocumentMedia): string {
    if (!this.isLocalMediaFileId(media.fileId)) {
      return media.fileId;
    }
    return this.localImagePreviewUrls.get(media.fileId) || this.brokenImagePath;
  }

  public getSelectedFilePreview(file: File): string {
    const existing = this.localFilePreviewUrls.get(file);
    if (existing) {
      return existing;
    }

    const preview = URL.createObjectURL(file);
    this.localFilePreviewUrls.set(file, preview);
    return preview;
  }

  public canAddDocument(): boolean {
    return this.hasValidFolderSelection()
      && this.newDocumentFolderName.trim().length > 0
      && this.newDocumentName.trim().length > 0
      && this.newDocumentDate.trim().length > 0;
  }

  public canSaveDocumentDetails(): boolean {
    if (!this.selectedDocumentDraft) {
      return false;
    }

    return this.selectedDocumentDraft.folderCategory.trim().length > 0
      && this.selectedDocumentDraft.folderType.trim().length > 0
      && this.selectedDocumentDraft.folderName.trim().length > 0
      && this.selectedDocumentDraft.documentName.trim().length > 0
      && this.selectedDocumentDraft.documentDate.trim().length > 0;
  }

  public async addDocument(): Promise<void> {
    this.newDocumentFormInteracted = true;
    this.newDocumentSubmitAttempted = true;
    if (!this.canEdit || !this.canAddDocument()) {
      this.cdr.markForCheck();
      return;
    }

    const now = new Date().toISOString();
    const currentUserId = this.getCurrentUserId();
    const currentUserFriendlyName = this.authService.currentUserFriendlyName;
    const documentId = uuidv4();
    const newDoc: InternalDocumentItem = {
      id: documentId,
      __source: {},
      isNew: true,
      folderCategory: this.newDocumentCategory.trim(),
      folderType: this.newDocumentType.trim(),
      folderUuid: this.buildFolderUuid(),
      folderName: this.newDocumentFolderName.trim(),
      userId: currentUserId,
      userFriendlyName: currentUserFriendlyName,
      documentName: this.newDocumentName.trim(),
      documentDescription: this.newDocumentDescription.trim(),
      documentEntryDate: now,
      documentDate: this.newDocumentDate,
      appendix: this.newDocumentAppendix,
      media: [],
      selectedFiles: [...this.newDocumentSelectedFiles],
    };

    this.documents = [newDoc, ...this.documents];
    this.persistDocuments();
    this.resetNewDocumentForm();
  }

  public openDocumentDetails(doc: InternalDocumentItem): void {
    this.restoreFocusTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.selectedDocument = doc;
    this.selectedDocumentDraft = {
      ...doc,
      documentDate: this.toDateInputValue(doc.documentDate),
      media: [...doc.media],
      selectedFiles: [...doc.selectedFiles],
      __source: { ...doc.__source },
    };
    this.isDetailModalOpen = true;
    this.initFolderPicker('detail', doc.folderCategory, doc.folderType, doc.folderCategory);
    this.cdr.markForCheck();
    this.scheduleDetailModalFocus();
  }

  public requestCloseDocumentDetails(): void {
    if (this.isDiscardConfirmOpen) {
      return;
    }

    if (this.shouldConfirmDetailDismiss()) {
      this.openDiscardConfirm();
      return;
    }

    this.closeDocumentDetails();
  }

  public closeDocumentDetails(): void {
    this.isDiscardConfirmOpen = false;
    this.cleanupDiscardedDetailLocalMedia();
    this.isDetailModalOpen = false;
    this.selectedDocument = null;
    this.selectedDocumentDraft = null;
    this.cdr.markForCheck();
    this.restoreFocusAfterDetailModalClose();
  }

  public onDetailModalKeydown(event: KeyboardEvent): void {
    if (!this.isDetailModalOpen) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();

      if (this.isDiscardConfirmOpen) {
        this.cancelDiscardConfirm();
        return;
      }

      this.requestCloseDocumentDetails();
      return;
    }

    if (event.key === 'Tab') {
      if (this.isDiscardConfirmOpen) {
        this.trapDiscardConfirmFocus(event);
        return;
      }

      this.trapDetailModalFocus(event);
    }
  }

  public cancelDiscardConfirm(): void {
    this.isDiscardConfirmOpen = false;
    this.cdr.markForCheck();
    this.scheduleDetailModalFocus();
  }

  public confirmDiscardDocumentDetails(): void {
    this.isDiscardConfirmOpen = false;
    this.closeDocumentDetails();
  }

  public async saveDocumentDetails(): Promise<void> {
    if (!this.selectedDocument || !this.selectedDocumentDraft || !this.canEdit || !this.canSaveDocumentDetails()) {
      return;
    }

    const selectedDocument = this.selectedDocument;
    const previousMedia = [...selectedDocument.media];
    const previousSelectedFiles = [...selectedDocument.selectedFiles];
    const selectedDocumentDraft: InternalDocumentItem = {
      ...this.selectedDocumentDraft,
      media: [...this.selectedDocumentDraft.media],
      selectedFiles: [...this.selectedDocumentDraft.selectedFiles],
      __source: { ...this.selectedDocumentDraft.__source },
    };

    this.cleanupRemovedLocalMedia(previousMedia, selectedDocumentDraft.media);
    this.cleanupRemovedLocalFiles(previousSelectedFiles, selectedDocumentDraft.selectedFiles);

    const updated = this.documents.map((doc) => {
      if (doc.id !== selectedDocument.id) {
        return doc;
      }
      return {
        ...selectedDocumentDraft,
        __source: { ...doc.__source },
      };
    });

    this.documents = updated;
    this.persistDocuments();
    this.closeDocumentDetails();
  }

  public async deleteDocument(docId: string, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (!this.canEdit) {
      return;
    }

    const target = this.documents.find((doc) => doc.id === docId);
    if (!target || target.isDeleted) {
      return;
    }

    const confirmed = await this.miscService.showConfirmDialog(
      'DOCUMENTS_FIELD.DELETE_DOCUMENT_TITLE',
      'DOCUMENTS_FIELD.DELETE_DOCUMENT_MESSAGE',
      true,
      { name: target.documentName },
    );
    if (!confirmed) {
      return;
    }

    this.applyDocumentDeletion(target);

    if (this.selectedDocument?.id === docId) {
      this.closeDocumentDetails();
    }

    this.persistDocuments();
  }

  public moveDocument(docId: string, categoryId: string, typeId: string): void {
    if (!this.canEdit) {
      return;
    }

    this.documents = this.documents.map((doc) => {
      if (doc.id !== docId) {
        return doc;
      }
      return {
        ...doc,
        folderCategory: categoryId,
        folderType: typeId,
      };
    });

    this.persistDocuments();
  }

  public async deleteFolderDocuments(categoryId: string, typeId: string, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (!this.canEdit) {
      return;
    }

    const folderDocuments = this.getDocumentsForFolder(categoryId, typeId);
    if (folderDocuments.length === 0) {
      return;
    }

    const confirmed = await this.miscService.showConfirmDialog(
      'DOCUMENTS_FIELD.DELETE_FOLDER_TITLE',
      'DOCUMENTS_FIELD.DELETE_FOLDER_MESSAGE',
      true,
      { name: this.getTypeText(categoryId, typeId) },
    );
    if (!confirmed) {
      return;
    }

    for (const doc of folderDocuments) {
      this.applyDocumentDeletion(doc);
      if (this.selectedDocument?.id === doc.id) {
        this.closeDocumentDetails();
      }
    }

    this.persistDocuments();
  }

  public getCategoryBubbleClass(categoryId: string): string {
    return CATEGORY_BUBBLE_CLASS[categoryId] || 'category-default';
  }

  public isMine(doc: InternalDocumentItem): boolean {
    const currentUserId = this.getCurrentUserId();
    return currentUserId.length > 0 && doc.userId === currentUserId;
  }

  public getCategoryText(categoryId: string): string {
    return this.categoryOptions.find((item) => item.id === categoryId)?.text || categoryId;
  }

  public getTypeText(categoryId: string, typeId: string): string {
    return this.getTypesForCategory(categoryId).find((item) => item.id === typeId)?.text || typeId;
  }

  public formatDate(value: string): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString();
  }

  public formatDateTime(value: string): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString();
  }

  public truncate(value: string, max = 140): string {
    if (!value || value.length <= max) {
      return value;
    }
    return `${value.slice(0, max)}…`;
  }

  public getPreviewMedia(doc: InternalDocumentItem, max = 3): DocumentMedia[] {
    return doc.media.slice(0, max);
  }

  public getPreviewAttachments(doc: InternalDocumentItem, max = 3): DocumentAttachmentPreview[] {
    const attachments: DocumentAttachmentPreview[] = [];
    for (const media of doc.media) {
      attachments.push({
        id: media.fileId,
        name: media.originalname,
        mediaType: media.mediaType,
        isExisting: true,
        fileId: media.fileId,
      });
    }
    for (const file of doc.selectedFiles) {
      const id = this.getLocalFileId(file);
      attachments.push({
        id,
        name: file.name,
        mediaType: file.type || 'application/octet-stream',
        isExisting: false,
        file,
      });
    }
    return attachments.slice(0, max);
  }

  public getAttachmentCount(doc: InternalDocumentItem): number {
    return doc.media.length + doc.selectedFiles.length;
  }

  public getAllAttachments(doc: InternalDocumentItem): DocumentAttachmentPreview[] {
    return this.getPreviewAttachments(doc, Number.MAX_SAFE_INTEGER);
  }

  public hasMoreMedia(doc: InternalDocumentItem, max = 3): boolean {
    return this.getAttachmentCount(doc) > max;
  }

  public isImageMedia(mediaType: string): boolean {
    return mediaType.startsWith('image/');
  }

  public isPdfMedia(mediaType: string): boolean {
    return mediaType.includes('pdf');
  }

  public isEmailMedia(mediaType: string, fileName?: string): boolean {
    const normalizedType = (mediaType || '').toLowerCase();
    const normalizedName = (fileName || '').toLowerCase();
    return normalizedType.includes('message/rfc822')
      || normalizedType.includes('vnd.ms-outlook')
      || normalizedName.endsWith('.eml')
      || normalizedName.endsWith('.msg');
  }

  public getMediaBadgeIcon(media: Pick<DocumentMedia, 'mediaType' | 'originalname'>): string | null {
    if (this.isPdfMedia(media.mediaType)) {
      return 'fa-regular fa-file-pdf';
    }

    if (this.isEmailMedia(media.mediaType, media.originalname)) {
      return 'fa-regular fa-envelope';
    }

    return null;
  }

  public getMediaBadgeLabel(media: DocumentMedia): string {
    if (media.mediaType.startsWith('video/')) {
      return 'VIDEO';
    }
    if (media.mediaType.includes('pdf')) {
      return 'PDF';
    }
    if (media.mediaType.includes('sheet') || media.mediaType.includes('excel')) {
      return 'XLS';
    }
    if (media.mediaType.includes('word') || media.mediaType.includes('document')) {
      return 'DOC';
    }

    const extension = (media.originalname + '.').split('.').pop();
    return extension?.toUpperCase() || 'FILE';
  }

  public openAttachment(doc: InternalDocumentItem, attachment: DocumentAttachmentPreview, event?: Event): void {
    event?.stopPropagation();
    if (this.isImageMedia(attachment.mediaType)) {
      this.openCarouselForAttachment(doc, attachment);
      return;
    }
    if (this.isPdfMedia(attachment.mediaType)) {
      this.openPdfForAttachment(attachment);
      return;
    }
    if (this.isEmailMedia(attachment.mediaType, attachment.name)) {
      this.openEmailForAttachment(attachment);
      return;
    }
    void this.downloadAttachment(attachment);
  }

  public trackByCategory(_: number, category: DocumentFolderNode): string {
    return category.id;
  }

  public trackByFolderType(_: number, typeNode: DocumentFolderNode): string {
    return typeNode.id;
  }

  public trackByFolderName(_: number, folderGroup: DocumentFolderNameGroup): string {
    return folderGroup.folderName || '__unnamed__';
  }

  public trackByDocument(_: number, doc: InternalDocumentItem): string {
    return doc.id;
  }

  public canShowReminders(doc: InternalDocumentItem | null): boolean {
    return !!doc;
  }

  public getReminderAvailabilityMessage(doc: InternalDocumentItem | null): string {
    return '';
  }

  public getReminderCountForDocument(doc: InternalDocumentItem): number {
    return this.reminders.filter((reminder) => this.isReminderForDocument(reminder, doc.id) && !reminder.isDeleted).length;
  }

  public getCurrentClaimId(): string {
    return this.getClaimId();
  }

  public isReminderDue(doc: InternalDocumentItem): boolean {
    const reminders = this.reminders
      .filter((reminder) => this.isReminderForDocument(reminder, doc.id) && !reminder.isDeleted)
      .sort((left, right) => this.getReminderDateValue(left).localeCompare(this.getReminderDateValue(right)));
    if (reminders.length === 0) {
      return false;
    }
    const today = new Date().toISOString().slice(0, 10);
    const firstReminderDate = this.toDateInputValue(this.getReminderDateValue(reminders[0]));
    return firstReminderDate <= today;
  } 

  public getReminderToneClassForDocument(doc: InternalDocumentItem): string {
    const reminders = this.reminders
      .filter((reminder) => this.isReminderForDocument(reminder, doc.id) && !reminder.isDeleted)
      .sort((left, right) => this.getReminderDateValue(left).localeCompare(this.getReminderDateValue(right)));

    if (reminders.length === 0) {
      return 'doc-reminder-icon--hidden';
    }

    const firstReminderDate = this.toDateInputValue(this.getReminderDateValue(reminders[0]));
    const today = new Date().toISOString().slice(0, 10);
    if (firstReminderDate < today) {
      return 'doc-reminder-icon--danger';
    }
    if (firstReminderDate === today) {
      return 'doc-reminder-icon--warning';
    }
    return 'doc-reminder-icon--default';
  }

  public getReminderTooltip(doc: InternalDocumentItem): string {
    const reminders = this.reminders
      .filter((reminder) => this.isReminderForDocument(reminder, doc.id) && !reminder.isDeleted)
      .sort((left, right) => this.getReminderDateValue(left).localeCompare(this.getReminderDateValue(right)));

    if (reminders.length === 0) {
      return '';
    }

    return reminders
      .map((reminder) => {
        const dateLabel = this.formatDate(this.getReminderDateValue(reminder));
        const note = this.getReminderNoteValue(reminder).trim();
        return note ? `${dateLabel}: ${note}` : dateLabel;
      })
      .join('\n\n');
  }

  public onDocumentRemindersChange(nextReminders: Reminder[]): void {
    this.reminders = this.normalizeReminderDrafts(nextReminders);
    this.syncReminderDraftsToModel();
    this.markReminderChangesAsDirty();
    this.cdr.markForCheck();
  }

  private scheduleDetailModalFocus(): void {
    window.setTimeout(() => {
      if (!this.isDetailModalOpen) {
        return;
      }

      const focusable = this.getDetailModalFocusableElements();
      if (focusable.length > 0) {
        this.focusElement(focusable[0]);
        return;
      }

      const dialog = this.detailModalDialog?.nativeElement;
      if (dialog) {
        dialog.focus();
      }
    });
  }

  private trapDetailModalFocus(event: KeyboardEvent): void {
    const focusable = this.getDetailModalFocusableElements();
    const dialog = this.detailModalDialog?.nativeElement;

    if (!dialog) {
      return;
    }

    if (focusable.length === 0) {
      event.preventDefault();
      dialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const activeElement = document.activeElement as HTMLElement | null;

    if (event.shiftKey) {
      if (!activeElement || activeElement === first || activeElement === dialog) {
        event.preventDefault();
        this.focusElement(last);
      }
      return;
    }

    if (!activeElement || activeElement === last) {
      event.preventDefault();
      this.focusElement(first);
    }
  }

  private openDiscardConfirm(): void {
    this.isDiscardConfirmOpen = true;
    this.cdr.markForCheck();
    this.scheduleDiscardConfirmFocus();
  }

  private scheduleDiscardConfirmFocus(): void {
    window.setTimeout(() => {
      if (!this.isDiscardConfirmOpen) {
        return;
      }

      const focusable = this.getDiscardConfirmFocusableElements();
      if (focusable.length > 0) {
        this.focusElement(focusable[0]);
        return;
      }

      this.discardConfirmDialog?.nativeElement?.focus();
    });
  }

  private trapDiscardConfirmFocus(event: KeyboardEvent): void {
    const focusable = this.getDiscardConfirmFocusableElements();
    const dialog = this.discardConfirmDialog?.nativeElement;

    if (!dialog) {
      return;
    }

    if (focusable.length === 0) {
      event.preventDefault();
      dialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const activeElement = document.activeElement as HTMLElement | null;

    if (event.shiftKey) {
      if (!activeElement || activeElement === first || activeElement === dialog) {
        event.preventDefault();
        this.focusElement(last);
      }
      return;
    }

    if (!activeElement || activeElement === last) {
      event.preventDefault();
      this.focusElement(first);
    }
  }

  private getDetailModalFocusableElements(): HTMLElement[] {
    return this.getFocusableElements(this.detailModalDialog?.nativeElement);
  }

  private getDiscardConfirmFocusableElements(): HTMLElement[] {
    return this.getFocusableElements(this.discardConfirmDialog?.nativeElement);
  }

  private getFocusableElements(container?: HTMLElement): HTMLElement[] {
    if (!container) {
      return [];
    }

    const selector = [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      'ion-button:not([disabled])',
      'ion-input:not([disabled])',
      'ion-select:not([disabled])',
      'ion-textarea:not([disabled])',
      'ion-checkbox:not([disabled])',
    ].join(',');

    return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter((element) => {
      if (element === container) {
        return false;
      }

      const ariaHidden = element.getAttribute('aria-hidden');
      const isHidden = ariaHidden === 'true' || element.hasAttribute('hidden');
      return !isHidden && element.tabIndex !== -1;
    });
  }

  private focusElement(element: HTMLElement): void {
    const focusableElement = element as HTMLElement & { setFocus?: () => Promise<void> | void };
    if (typeof focusableElement.setFocus === 'function') {
      void focusableElement.setFocus();
      return;
    }

    focusableElement.focus();
  }

  private restoreFocusAfterDetailModalClose(): void {
    const target = this.restoreFocusTarget;
    this.restoreFocusTarget = null;

    if (!target || !document.contains(target)) {
      return;
    }

    window.setTimeout(() => {
      if (document.contains(target)) {
        target.focus();
      }
    });
  }

  private shouldConfirmDetailDismiss(): boolean {
    return this.canEdit && this.isDetailModalDirty();
  }

  private isDetailModalDirty(): boolean {
    if (!this.selectedDocument || !this.selectedDocumentDraft) {
      return false;
    }

    return this.selectedDocument.folderCategory !== this.selectedDocumentDraft.folderCategory
      || this.selectedDocument.folderType !== this.selectedDocumentDraft.folderType
      || this.selectedDocument.folderName !== this.selectedDocumentDraft.folderName
      || this.selectedDocument.documentName !== this.selectedDocumentDraft.documentName
      || this.selectedDocument.documentDescription !== this.selectedDocumentDraft.documentDescription
      || this.toDateInputValue(this.selectedDocument.documentDate) !== this.selectedDocumentDraft.documentDate
      || this.selectedDocument.appendix !== this.selectedDocumentDraft.appendix
      || !this.areDocumentMediaListsEqual(this.selectedDocument.media, this.selectedDocumentDraft.media)
      || !this.areSelectedFilesEqual(this.selectedDocument.selectedFiles, this.selectedDocumentDraft.selectedFiles);
  }

  private areDocumentMediaListsEqual(left: DocumentMedia[], right: DocumentMedia[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    return left.every((media, index) => {
      const candidate = right[index];
      return !!candidate
        && media.fileId === candidate.fileId
        && media.originalname === candidate.originalname
        && media.mediaType === candidate.mediaType;
    });
  }

  private areSelectedFilesEqual(left: File[], right: File[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    return left.every((file, index) => {
      const candidate = right[index];
      return !!candidate
        && file.name === candidate.name
        && file.type === candidate.type
        && file.size === candidate.size
        && file.lastModified === candidate.lastModified;
    });
  }

  private getAttachmentUrl(attachment: DocumentAttachmentPreview): string | null {
    if (attachment.isExisting && attachment.fileId) {
      return `${environment.apiUrl}/medias/${attachment.fileId}/download`;
    }
    if (!attachment.isExisting && attachment.file) {
      return this.getSelectedFilePreview(attachment.file);
    }
    return null;
  }

  private async openCarouselForAttachment(doc: InternalDocumentItem, attachment: DocumentAttachmentPreview): Promise<void> {
    const images = this.getAllImageMediaForDocument(doc);
    if (images.length === 0) {
      return;
    }
    const initialIndex = this.findImageIndex(images, attachment);
    await this.openCarouselModal(images, initialIndex);
  }

  private async openPdfForAttachment(attachment: DocumentAttachmentPreview): Promise<void> {
    const url = this.getAttachmentUrl(attachment);
    if (!url) {
      return;
    }
    await this.openPdfModal(url);
  }

  private async openEmailForAttachment(attachment: DocumentAttachmentPreview): Promise<void> {
    await this.openEmailModal(attachment);
  }

  private async downloadAttachment(attachment: DocumentAttachmentPreview): Promise<void> {
    if (attachment.isExisting && attachment.fileId) {
      try {
        await firstValueFrom(this.mediaService.downloadMedia(attachment.fileId, attachment.name));
      } catch {
        this.miscService.showErrorToast('ERRORS.GENERIC', true);
      }
      return;
    }
    if (attachment.file) {
      const url = this.getSelectedFilePreview(attachment.file);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  private getAllImageMediaForDocument(doc: InternalDocumentItem): Medium[] {
    const images: Medium[] = [];
    for (const media of doc.media) {
      if (this.isImageMedia(media.mediaType)) {
        images.push({ fileId: media.fileId, mediaType: media.mediaType, originalname: media.originalname });
      }
    }
    for (const file of doc.selectedFiles) {
      if (this.isImageMedia(file.type || '')) {
        images.push({ fileId: this.getSelectedFilePreview(file), mediaType: file.type, originalname: file.name });
      }
    }
    return images;
  }

  private findImageIndex(images: Medium[], attachment: DocumentAttachmentPreview): number {
    if (attachment.isExisting && attachment.fileId) {
      const idx = images.findIndex((img) => img.fileId === attachment.fileId);
      return idx >= 0 ? idx : 0;
    }
    if (!attachment.isExisting && attachment.file) {
      const url = this.getSelectedFilePreview(attachment.file);
      const idx = images.findIndex((img) => img.fileId === url);
      return idx >= 0 ? idx : 0;
    }
    return 0;
  }

  private async openCarouselModal(media: Medium[], initialIndex: number): Promise<void> {
    try {
      await this.ngZone.runOutsideAngular(async () => {
        const modal = await this.modalController.create({
          component: CarouselModalComponent,
          componentProps: {
            media,
            initialSlide: initialIndex,
          },
          cssClass: 'resizable-modal',
          animated: true,
          backdropDismiss: true,
        });
        this.applySavedModalSize(modal, 'carouselModalSize', 0.9, 0.8);
        await modal.present();
        this.applySavedModalSize(modal, 'carouselModalSize', 0.9, 0.8);
      });
    } catch (error) {
      console.error('Error opening carousel modal:', error);
    }
  }

  private async openPdfModal(fileId: string): Promise<void> {
    try {
      const modal = await this.modalController.create({
        component: PdfViewerComponent,
        componentProps: {
          fileId,
        },
        cssClass: 'resizable-modal',
        animated: true,
        backdropDismiss: true,
      });
      this.applySavedModalSize(modal, 'pdfModalSize', 0.8, 0.75);
      await modal.present();
      this.applySavedModalSize(modal, 'pdfModalSize', 0.8, 0.75);
    } catch (error) {
      console.error('Error opening pdf viewer:', error);
    }
  }

  private async openEmailModal(attachment: DocumentAttachmentPreview): Promise<void> {
    try {
      const modal = await this.modalController.create({
        component: EmailViewerComponent,
        componentProps: {
          mediaId: attachment.isExisting ? attachment.fileId || null : null,
          fileUrl: attachment.isExisting ? this.getAttachmentUrl(attachment) : null,
          file: attachment.isExisting ? null : attachment.file || null,
          fileName: attachment.name,
          mediaType: attachment.mediaType,
        },
        cssClass: 'resizable-modal',
        animated: true,
        backdropDismiss: true,
      });
      this.applySavedModalSize(modal, 'emailModalSize', 0.82, 0.8);
      await modal.present();
      this.applySavedModalSize(modal, 'emailModalSize', 0.82, 0.8);
    } catch (error) {
      console.error('Error opening email viewer:', error);
    }
  }

  private applySavedModalSize(modal: HTMLIonModalElement, storageKey: string, widthRatio: number, heightRatio: number): void {
    const fallbackWidth = Math.round(window.innerWidth * widthRatio);
    const fallbackHeight = Math.round(window.innerHeight * heightRatio);
    const size = this.readModalSize(storageKey);
    const width = size?.width ?? fallbackWidth;
    const height = size?.height ?? fallbackHeight;
    modal.style.setProperty('--width', `${width}px`);
    modal.style.setProperty('--height', `${height}px`);
    modal.style.setProperty('--min-width', '320px');
    modal.style.setProperty('--min-height', '240px');
  }

  private readModalSize(storageKey: string): { width: number; height: number } | null {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as { width?: number; height?: number };
      if (!parsed.width || !parsed.height) {
        return null;
      }
      return { width: parsed.width, height: parsed.height };
    } catch {
      return null;
    }
  }

  private subscribeToModelChanges(): void {
    this.formControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.updateFromControlValue(value);
      });
  }

  private updateFromControlValue(value: unknown): void {
    const docs = Array.isArray(value) ? value : [];
    this.documents = docs
      .filter((item): item is Record<string, unknown> => this.isRecord(item))
      .map((item) => this.normalizeDocument(item));

    const stagedReminders = this.getStagedReminders();
    this.reminders = stagedReminders ? this.normalizeReminderDrafts(stagedReminders) : [];

    this.computeChronologyDocuments();
    this.cdr.markForCheck();
  }

  private loadFolderTree(): void {
    const propTree = this.field.props?.['folderTree'];
    if (Array.isArray(propTree)) {
      this.folderTree = this.toFolderTree(propTree);
      this.rebuildFolderOptions();
      this.cdr.markForCheck();
      return;
    }

    const settingKey = this.getSettingKey();
    const clientId = this.contextService.getCurrentClientId() || '*';
    const subsidiaryId = this.contextService.getCurrentSubsidiaryId() || '*';

    this.settingsService
      .getSettingByKey(settingKey, clientId, subsidiaryId, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (payload) => {
          this.folderTree = this.extractFolderTree(payload);
          this.rebuildFolderOptions();
          this.cdr.markForCheck();
        },
        error: () => {
          this.folderTree = [];
          this.rebuildFolderOptions();
          this.cdr.markForCheck();
        },
      });
  }

  private rebuildFolderOptions(): void {
    const categories: DocumentCategoryOption[] = [];
    const types: DocumentTypeOption[] = [];
    const byCategory: Record<string, DocumentTypeOption[]> = {};
    const folderOptions: FolderTypeOption[] = [];

    for (const category of this.folderTree) {
      categories.push({
        id: category.id,
        text: category.text,
      });

      const children = category.children || [];
      byCategory[category.id] = [];
      for (const child of children) {
        const item: DocumentTypeOption = {
          id: child.id,
          text: child.text,
          categoryId: category.id,
        };
        types.push(item);
        byCategory[category.id].push(item);
        folderOptions.push({
          categoryId: category.id,
          typeId: child.id,
          categoryText: category.text,
          typeText: child.text,
        });
      }

      this.folderTypeVisibleCount[category.id] = this.visibleFolderTypeCount;
    }

    this.categoryOptions = categories;
    this.typeOptions = types;
    this.typeOptionsByCategory = byCategory;
    this.folderTypeOptions = folderOptions;
    this.folderTreeNodes = this.toFolderSelectionTree();
    if (this.folderTreeNodes.length > 0) {
      const expanded = this.folderTreeState.expandedNodeIds || {};
      this.folderTreeState.expandedNodeIds = { ...expanded, [this.folderTreeRootId]: true };
    }

    this.harmonizeDocumentsWithFolderTree();
    this.syncNewDocumentFolderSelectionState();
    this.scheduleFolderTreeOverlayPosition();
  }

  private computeChronologyDocuments(): void {
    const docs = this.documents
      .filter((doc) => this.isDocumentVisible(doc))
      .filter((doc) => this.matchesSearch(doc))
      .filter((doc) => this.matchesChronologyFilters(doc))
      .sort((a, b) => this.getDateMs(b.documentEntryDate) - this.getDateMs(a.documentEntryDate));

    this.chronologyDocuments = docs;
  }

  private harmonizeDocumentsWithFolderTree(): void {
    if (this.documents.length === 0 || this.folderTree.length === 0) {
      return;
    }

    const mappedDocuments = this.documents.map((doc) => {
      const normalizedCategory = this.normalizeCategoryId(doc.folderCategory);
      const normalizedType = this.normalizeTypeId(normalizedCategory, doc.folderType);

      if (normalizedCategory === doc.folderCategory && normalizedType === doc.folderType) {
        return doc;
      }

      return {
        ...doc,
        folderCategory: normalizedCategory,
        folderType: normalizedType,
      };
    });

    this.documents = mappedDocuments;
    this.computeChronologyDocuments();
  }

  private normalizeCategoryId(categoryId: string): string {
    if (this.typeOptionsByCategory[categoryId]) {
      return categoryId;
    }

    if (categoryId.toUpperCase() === 'INSURER' && this.typeOptionsByCategory['brokerInsurer']) {
      return 'brokerInsurer';
    }

    const caseInsensitive = Object.keys(this.typeOptionsByCategory)
      .find((existingCategoryId) => existingCategoryId.toLowerCase() === categoryId.toLowerCase());

    return caseInsensitive || categoryId;
  }

  private normalizeTypeId(categoryId: string, typeId: string): string {
    const categoryTypes = this.getTypesForCategory(categoryId);
    if (categoryTypes.length === 0) {
      return typeId;
    }

    if (categoryTypes.some((type) => type.id === typeId)) {
      return typeId;
    }

    const caseInsensitiveMatch = categoryTypes.find((type) => type.id.toLowerCase() === typeId.toLowerCase());
    if (caseInsensitiveMatch) {
      return caseInsensitiveMatch.id;
    }

    const suffixMatch = this.extractTypeSuffix(typeId);
    if (!suffixMatch) {
      return typeId;
    }

    const suffixBased = categoryTypes.find((type) => this.extractTypeSuffix(type.id) === suffixMatch);
    return suffixBased ? suffixBased.id : typeId;
  }

  private extractTypeSuffix(typeId: string): string {
    const match = typeId.match(/_(\d{4})$/);
    return match ? match[1] : '';
  }

  private matchesSearch(doc: InternalDocumentItem): boolean {
    const term = this.searchText.trim().toLowerCase();
    if (!term) {
      return true;
    }

    const haystack = `${doc.documentName} ${this.toPlainText(doc.documentDescription)}`.toLowerCase();
    return haystack.includes(term);
  }

  public sanitizeDocumentDescription(value: string): string {
    return this.domSanitizer.sanitize(SecurityContext.HTML, value || '') || '';
  }

  public toPlainText(value: string): string {
    return (value || '')
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private matchesChronologyFilters(doc: InternalDocumentItem): boolean {
    if (this.chronologyCategoryFilter && doc.folderCategory !== this.chronologyCategoryFilter) {
      return false;
    }

    if (this.chronologyTypeFilter && doc.folderType !== this.chronologyTypeFilter) {
      return false;
    }

    const docDate = this.getDateMs(doc.documentDate || doc.documentEntryDate);

    if (this.chronologyFromDate) {
      const fromDate = this.getDateMs(this.chronologyFromDate);
      if (docDate < fromDate) {
        return false;
      }
    }

    if (this.chronologyToDate) {
      const toDate = this.getDateMs(this.chronologyToDate);
      if (docDate > toDate + (24 * 60 * 60 * 1000 - 1)) {
        return false;
      }
    }

    return true;
  }

  private persistDocuments(): void {
    const output = this.documents.map((doc) => this.toExternalDocument(doc));
    this.formControl.setValue(output);
    this.syncModelAtFieldKey(output);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
    this.onChange(output);
    this.computeChronologyDocuments();
    this.cdr.markForCheck();
  }

  private syncReminderDraftsToModel(): void {
    const reminderModelPath = this.getReminderModelPath();
    if (!reminderModelPath || !this.isRecord(this.model)) {
      return;
    }

    const reminderDrafts = this.reminders.map((reminder) => ({
      ...reminder,
      createdAt: reminder.createdAt ? new Date(reminder.createdAt) : undefined,
      updatedAt: reminder.updatedAt ? new Date(reminder.updatedAt) : undefined,
      jsonPayload: { ...reminder.jsonPayload },
    }));

    this.miscService.setDeepChildValue(this.model, reminderModelPath, reminderDrafts);
  }


  private replaceDocument(updatedDocument: InternalDocumentItem): void {
    let hasChanged = false;
    this.documents = this.documents.map((doc) => {
      if (doc.id !== updatedDocument.id) {
        return doc;
      }
      hasChanged = true;
      return updatedDocument;
    });

    if (hasChanged) {
      if (this.selectedDocument?.id === updatedDocument.id) {
        this.selectedDocument = updatedDocument;
      }
      if (this.selectedDocumentDraft?.id === updatedDocument.id) {
        this.selectedDocumentDraft = {
          ...updatedDocument,
          media: [...updatedDocument.media],
          selectedFiles: [...updatedDocument.selectedFiles],
          __source: { ...updatedDocument.__source },
        };
      }
      this.persistDocuments();
    }
  }

  private getClaimId(): string {
    if (!this.isRecord(this.model)) {
      return '';
    }
    return this.readString(this.model['id']) || this.readString(this.model['claimId']);
  }

  private getReminderModelPath(): string {
    if (!this.field.key) {
      return '';
    }
    return `jsonPayload.surrogatePayload.reminders`
  }

  private getStagedReminders(): Reminder[] | null {
    const reminderModelPath = this.getReminderModelPath();
    if (!reminderModelPath || !this.isRecord(this.model)) {
      return null;
    }

    const reminders = this.miscService.getDeepChildValue(this.model, reminderModelPath) as unknown;
    if (!Array.isArray(reminders)) {
      return null;
    }

    return reminders.filter((item): item is Reminder => this.isReminderRecord(item));
  }

  private isReminderForDocument(reminder: Reminder, documentId: string): boolean {
    return reminder.hostType === ReminderHostType.CLAIMS
      && reminder.hostId === this.getClaimId()
      && this.getReminderDocumentId(reminder) === documentId;
  }

  private getReminderDateValue(reminder: Reminder): string {
    return reminder.jsonPayload.reminderDate || '';
  }

  private getReminderNoteValue(reminder: Reminder): string {
    return reminder.jsonPayload.note || '';
  }

  private getReminderDocumentId(reminder: Reminder): string {
    return (reminder.jsonPayload as Reminder['jsonPayload'] & { documentId?: string }).documentId || '';
  }

  private isReminderRecord(value: unknown): value is Reminder {
    if (!this.isRecord(value)) {
      return false;
    }

    return typeof value['id'] === 'string'
      && this.isRecord(value['jsonPayload']);
  }

  private normalizeReminderDrafts(reminders: Reminder[]): Reminder[] {
    const claimId = this.getClaimId();

    return reminders
      .filter((reminder) => this.isReminderRecord(reminder))
      .map((reminder) => ({
        ...reminder,
        clientId: reminder.clientId || this.contextService.getCurrentClientId() || '',
        subsidiaryId: reminder.subsidiaryId ?? this.contextService.getCurrentSubsidiaryId() ?? null,
        userId: reminder.userId || this.getCurrentUserId(),
        hostType: ReminderHostType.CLAIMS,
        hostId: claimId || reminder.hostId || '',
        jsonPayload: { ...reminder.jsonPayload },
      }));
  }

  private markReminderChangesAsDirty(): void {
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
    this.field.form?.markAsDirty();
    this.field.form?.markAsTouched();
  }

  private applyDocumentDeletion(doc: InternalDocumentItem): void {
    this.markDocumentRemindersDeleted(doc.id);

    if (doc.isNew) {
      this.cleanupLocalMediaPreviewsForDocument(doc);
      this.documents = this.documents.filter((item) => item.id !== doc.id);
      return;
    }

    this.documents = this.documents.map((item) => {
      if (item.id !== doc.id) {
        return item;
      }

      return {
        ...item,
        isDeleted: true,
        __source: {
          ...item.__source,
          isDeleted: true,
        },
      };
    });
  }

  private isDocumentVisible(doc: InternalDocumentItem): boolean {
    return !doc.isDeleted;
  }

  private markDocumentRemindersDeleted(docId: string): void {
    let hasChanges = false;
    const nextReminders = this.reminders.map((reminder) => {
      if (!this.isReminderForDocument(reminder, docId) || reminder.isDeleted) {
        return reminder;
      }

      hasChanges = true;
      return {
        ...reminder,
        isActive: false,
        isDeleted: true,
        updatedAt: new Date(),
      };
    });

    if (!hasChanges) {
      return;
    }

    this.reminders = nextReminders;
    this.syncReminderDraftsToModel();
    this.markReminderChangesAsDirty();
  }

  private toDateInputValue(value: string): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    return parsed.toISOString().slice(0, 10);
  }

  private syncModelAtFieldKey(value: unknown): void {
    if (!this.field.key) {
      return;
    }

    const keys = Array.isArray(this.field.key)
      ? this.field.key.map((key) => String(key))
      : String(this.field.key).split('.');

    let cursor: Record<string, unknown> = this.model as Record<string, unknown>;
    for (let index = 0; index < keys.length - 1; index += 1) {
      const key = keys[index];
      const current = cursor[key];
      if (typeof current !== 'object' || current === null || Array.isArray(current)) {
        cursor[key] = {};
      }
      cursor = cursor[key] as Record<string, unknown>;
    }

    cursor[keys[keys.length - 1]] = value;
  }

  private resetNewDocumentForm(): void {
    this.newDocumentName = '';
    this.newDocumentDescription = '';
    this.newDocumentDate = this.getTodayDateInputValue();
    this.newDocumentAppendix = false;
    this.newDocumentFolderName = '';
    this.newDocumentFormInteracted = false;
    this.newDocumentSubmitAttempted = false;
    this.cleanupLocalFilePreviews(this.newDocumentSelectedFiles);
    this.newDocumentSelectedFiles = [];
    this.clearNewDocumentFolderSelection();
    this.cdr.markForCheck();
  }

  private clearNewDocumentFolderSelection(): void {
    this.newDocumentCategory = '';
    this.newDocumentType = '';
    this.newDocumentFolderSelection = '';
    this.selectedFolderNodeId = null;
    this.folderTreeState.selectedNodeIds = {};
    this.isFolderTreeCollapsed = true;
  }

  private syncNewDocumentFolderSelectionState(): void {
    if (!this.hasValidFolderSelection()) {
      this.clearNewDocumentFolderSelection();
      return;
    }

    this.newDocumentFolderSelection = this.buildFolderSelectionValue(this.newDocumentCategory, this.newDocumentType);
    this.selectedFolderNodeId = this.newDocumentFolderSelection;
    this.folderTreeState.selectedNodeIds = { [this.selectedFolderNodeId]: true };
    this.isFolderTreeCollapsed = true;
  }

  private getTodayDateInputValue(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private getSettingKey(): string {
    const key = this.field.props?.['settingKey'];
    if (typeof key === 'string' && key.trim().length > 0) {
      return key.trim();
    }
    return this.defaultSettingKey;
  }

  private getDefaultViewMode(): DocumentViewMode {
    return this.field.props?.['defaultView'] || 'chronology' as DocumentViewMode;
  }

  private getCurrentUserId(): string {
    // const explicitUserId = this.field.props?.['currentUserId'];
    // if (typeof explicitUserId === 'string' && explicitUserId.trim().length > 0) {
    //   return explicitUserId.trim();
    // }

    return this.authService.currentUserValue?.id || '';
  }

  private getMaxVisibleDocumentsPerFolder(): number {
    const value = this.field.props?.['maxDocumentsPerFolder'];
    if (typeof value === 'number' && value > 0) {
      return Math.floor(value);
    }
    return 5;
  }

  private getMaxVisibleFolderTypes(): number {
    const value = this.field.props?.['maxSubfoldersPerCategory'];
    if (typeof value === 'number' && value > 0) {
      return Math.floor(value);
    }
    return 5;
  }

  private getPropertyMap(): DocumentPropertyMap {
    const input = this.field.props?.['documentPropertyMap'];
    if (!this.isRecord(input)) {
      return DEFAULT_PROPERTY_MAP;
    }

    const mapped: DocumentPropertyMap = { ...DEFAULT_PROPERTY_MAP };
    const keys = Object.keys(DEFAULT_PROPERTY_MAP) as (keyof DocumentPropertyMap)[];

    for (const key of keys) {
      const incoming = input[key];
      if (typeof incoming === 'string' && incoming.trim().length > 0) {
        mapped[key] = incoming;
      }
    }

    return mapped;
  }

  private normalizeDocument(raw: Record<string, unknown>): InternalDocumentItem {
    const mapping = this.getPropertyMap();
    const mediaRaw = raw[mapping.media];
    const media = Array.isArray(mediaRaw)
      ? mediaRaw.filter((item): item is Record<string, unknown> => this.isRecord(item)).map((item) => ({
        fileId: this.readString(item['fileId']),
        originalname: this.readString(item['originalname']),
        mediaType: this.readString(item['mediaType']),
      }))
      : [];
    const selectedFilesRaw = raw[mapping.selectedFiles];
    const selectedFiles = Array.isArray(selectedFilesRaw)
      ? selectedFilesRaw.filter((item): item is File => item instanceof File)
      : [];

    this.ensureLocalFilePreviews(selectedFiles);

    const doc: InternalDocumentItem = {
      id: this.readString(raw['id']) || uuidv4(),
      __source: { ...raw },
      folderCategory: this.readString(raw[mapping.folderCategory]),
      folderType: this.readString(raw[mapping.folderType]),
      folderUuid: this.readString(raw[mapping.folderUuid]),
      folderName: this.readString(raw[mapping.folderName]),
      userId: this.readString(raw[mapping.userId]),
      userFriendlyName: this.readString(raw[mapping.userFriendlyName]),
      documentName: this.readString(raw[mapping.documentName]),
      documentDescription: this.readString(raw[mapping.documentDescription]),
      documentEntryDate: this.readString(raw[mapping.documentEntryDate]),
      documentDate: this.toDateInputValue(this.readString(raw[mapping.documentDate])),
      appendix: this.readBoolean(raw[mapping.appendix]),
      isDeleted: this.readBoolean(raw['isDeleted']),
      media,
      selectedFiles,
    };

    if (!doc.documentEntryDate) {
      doc.documentEntryDate = new Date().toISOString();
    }
    if (!doc.userId) {
      doc.userId = this.getCurrentUserId();
    }
    if (!doc.userFriendlyName) {
      doc.userFriendlyName = this.authService.currentUserFriendlyName;
    }

    return doc;
  }

  private toExternalDocument(doc: InternalDocumentItem): Record<string, unknown> {
    const mapping = this.getPropertyMap();
    const output: Record<string, unknown> = { ...doc.__source };
    for (const key in DEFAULT_PROPERTY_MAP) {
      output[key] = doc[key as keyof InternalDocumentItem];
    }
    // output[mapping.id] = doc.id;
    // output[mapping.folderCategory] = doc.folderCategory;
    // output[mapping.folderType] = doc.folderType;
    // output[mapping.folderUuid] = doc.folderUuid;
    // output[mapping.folderName] = doc.folderName;
    // output[mapping.userId] = doc.userId;
    // output[mapping.userFriendlyName] = doc.userFriendlyName;
    // output[mapping.documentName] = doc.documentName;
    // output[mapping.documentDescription] = doc.documentDescription;
    // output[mapping.documentEntryDate] = doc.documentEntryDate;
    // output[mapping.documentDate] = doc.documentDate;
    // output[mapping.appendix] = doc.appendix;
    output[mapping.media] = doc.media.map((item) => ({
      fileId: item.fileId,
      originalname: item.originalname,
      mediaType: item.mediaType,
    }));
    output[mapping.selectedFiles] = doc.selectedFiles;
    if (doc.isDeleted) {
      output['isDeleted'] = true;
    }

    return output;
  }

  private extractFolderTree(payload: unknown): DocumentFolderNode[] {
    if (this.isRecord(payload) && Array.isArray(payload['data'])) {
      return this.toFolderTree(payload['data']);
    }

    if (Array.isArray(payload)) {
      const first = payload[0];
      if (this.isRecord(first) && Array.isArray(first['value'])) {
        return this.toFolderTree(first['value']);
      }
      return this.toFolderTree(payload);
    }

    if (this.isRecord(payload)) {
      const maybeValue = payload['value'];
      if (Array.isArray(maybeValue)) {
        return this.toFolderTree(maybeValue);
      }
    }

    return [];
  }

  private toFolderTree(raw: unknown[]): DocumentFolderNode[] {
    return raw
      .filter((item): item is Record<string, unknown> => this.isRecord(item))
      .map((item) => {
        const text = this.readString(item['text']) || this.readString(item['label']);
        const label = this.readString(item['label']) || text;

        const children = Array.isArray(item['children'])
          ? item['children']
            .filter((child): child is Record<string, unknown> => this.isRecord(child))
            .map((child) => {
              const childText = this.readString(child['text']) || this.readString(child['label']);
              const childLabel = this.readString(child['label']) || childText;
              return {
                id: this.readString(child['id']),
                text: childText,
                label: childLabel,
              };
            })
          : [];

        return {
          id: this.readString(item['id']),
          text,
          label,
          children,
        };
      })
      .filter((node) => node.id.length > 0 && node.text.length > 0);
  }

  private getFolderKey(categoryId: string, typeId: string): string {
    return `${categoryId}::${typeId}`;
  }

  private getFolderNameKey(categoryId: string, typeId: string, folderName: string): string {
    return `${this.getFolderKey(categoryId, typeId)}::${folderName || '__unnamed__'}`;
  }

  private getDateMs(value: string): number {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 0;
    }
    return date.getTime();
  }

  private buildInternalId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `doc_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
  }

  private buildFolderUuid(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `folder_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
  }

  private buildLocalFileId(file: File, index: number): string {
    return `local_${Date.now()}_${index}_${file.name}`;
  }

  private buildFolderSelectionValue(categoryId: string, typeId: string): string {
    if (!categoryId || !typeId) {
      return '';
    }
    return `${categoryId}::${typeId}`;
  }

  private toFolderSelectionTree(): DocumentTreeNode[] {
    return this.buildFolderPickerNodes();
  }

  private buildFolderPickerNodes(lockedCategoryId = ''): DocumentTreeNode[] {
    if (this.folderTree.length === 0) {
      return [];
    }

    if (lockedCategoryId) {
      const category = this.folderTree.find((item) => item.id === lockedCategoryId);
      if (!category) {
        return [];
      }

      return (category.children || []).map((child) => ({
        id: this.buildFolderSelectionValue(category.id, child.id),
        name: child.text,
        categoryId: category.id,
        typeId: child.id,
      }));
    }

    const children = this.folderTree.map((category) => ({
      id: category.id,
      name: category.text,
      categoryId: category.id,
      children: (category.children || []).map((child) => ({
        id: this.buildFolderSelectionValue(category.id, child.id),
        name: child.text,
        categoryId: category.id,
        typeId: child.id,
      })),
    }));

    return [{
      id: this.folderTreeRootId,
      name: '',
      children,
    }];
  }

  private createFolderPickerState(): FolderPickerState {
    return {
      nodes: [],
      treeState: {},
      selectedNodeId: null,
      collapsed: true,
      lockedCategoryId: '',
    };
  }

  private createFolderPickerOptions(scope: FolderPickerScope): ITreeOptions {
    return {
      allowDrag: () => false,
      allowDrop: () => false,
      actionMapping: this.getFolderPickerActionMapping(scope),
      idField: 'id',
      displayField: 'name',
      childrenField: 'children',
      nodeHeight: 23,
      levelPadding: 16,
      animateExpand: true,
      animateSpeed: 30,
      animateAcceleration: 1.2,
    };
  }

  private initFolderPicker(
    scope: FolderPickerScope,
    categoryId: string,
    typeId: string,
    lockedCategoryId = '',
  ): void {
    const picker = this.folderPickers[scope];
    picker.lockedCategoryId = lockedCategoryId;
    picker.nodes = this.buildFolderPickerNodes(lockedCategoryId);
    const selection = this.buildFolderSelectionValue(categoryId, typeId);
    picker.selectedNodeId = selection || null;
    picker.treeState = {
      selectedNodeIds: selection ? { [selection]: true } : {},
      expandedNodeIds: this.buildFolderPickerExpandedIds(lockedCategoryId),
    };
    picker.collapsed = this.hasValidFolderPickerSelection(categoryId, typeId);
  }

  private buildFolderPickerExpandedIds(lockedCategoryId = ''): Record<string, boolean> {
    if (lockedCategoryId) {
      return { [lockedCategoryId]: true };
    }

    return { [this.folderTreeRootId]: true };
  }

  private ensureFolderPickerExpanded(scope: FolderPickerScope): void {
    const picker = this.folderPickers[scope];
    if (picker.nodes.length === 0) {
      return;
    }

    picker.treeState = {
      ...picker.treeState,
      expandedNodeIds: this.buildFolderPickerExpandedIds(picker.lockedCategoryId),
    };
  }

  private onFolderPickerNodeClick(node: TreeNode, scope: FolderPickerScope): void {
    if (!this.canSelectFolderNode(node)) {
      return;
    }

    const categoryId = node.data.categoryId || '';
    const typeId = node.data.typeId || '';
    const selection = this.buildFolderSelectionValue(categoryId, typeId);
    const picker = this.folderPickers[scope];
    picker.selectedNodeId = selection || null;
    picker.treeState = {
      ...picker.treeState,
      selectedNodeIds: selection ? { [selection]: true } : {},
    };
    picker.collapsed = true;

    if (scope === 'newDocument') {
      this.onNewDocumentFolderSelectionChange(selection);
      return;
    }

    if (scope === 'dropCreate' && this.dropCreateDraft) {
      this.dropCreateDraft.folderCategory = categoryId;
      this.dropCreateDraft.folderType = typeId;
      this.cdr.markForCheck();
      return;
    }

    if (scope === 'detail' && this.selectedDocumentDraft) {
      this.selectedDocumentDraft.folderCategory = categoryId;
      this.selectedDocumentDraft.folderType = typeId;
      this.cdr.markForCheck();
    }
  }

  private ensureFolderTreeExpanded(): void {
    if (this.folderTreeNodes.length === 0) {
      return;
    }
    const expanded: Record<string, boolean> = {
      [this.folderTreeRootId]: true,
    };
    if (this.newDocumentCategory) {
      expanded[this.newDocumentCategory] = true;
    }
    this.folderTreeState.expandedNodeIds = expanded;
  }

  private scheduleFolderTreeOverlayPosition(): void {
    requestAnimationFrame(() => this.updateFolderTreeOverlayPosition());
  }

  private updateFolderTreeOverlayPosition(): void {
    if (!this.folderTreeLabel) {
      return;
    }
    const rect = this.folderTreeLabel.nativeElement.getBoundingClientRect();
    this.renderer.setStyle(this.hostRef.nativeElement, '--folder-tree-overlay-top', `${Math.round(rect.top)}px`);
    this.renderer.setStyle(this.hostRef.nativeElement, '--folder-tree-overlay-left', `${Math.round(rect.right)}px`);
  }

  private isFolderLeafNode(node: TreeNode): boolean {
    const hasChildren = typeof (node as any).hasChildren === 'function'
      ? (node as any).hasChildren()
      : (node?.data?.children?.length || node?.children?.length) > 0;
    return !hasChildren;
  }

  private getFolderTreeActionMapping(): IActionMapping {
    return this.getFolderPickerActionMapping('newDocument');
  }

  private getFolderPickerActionMapping(scope: FolderPickerScope): IActionMapping {
    return {
      mouse: {
        click: (_tree, node: TreeNode, event?: Event) => {
          const isLeaf = this.isFolderLeafNode(node);
          if (!isLeaf) {
            node.toggleExpanded();
            event?.preventDefault?.();
            return;
          }

          this.onFolderPickerNodeClick(node, scope);
        },
      },
    };
  }

  private parseFolderSelection(value: string): [string, string] {
    if (!value) {
      return ['', ''];
    }
    const [categoryId, typeId] = value.split('::');
    return [categoryId || '', typeId || ''];
  }

  private registerLocalMediaPreview(media: DocumentMedia, file: File): void {
    if (!this.isImageMedia(media.mediaType) || !this.isLocalMediaFileId(media.fileId)) {
      return;
    }

    const previous = this.localImagePreviewUrls.get(media.fileId);
    if (previous) {
      URL.revokeObjectURL(previous);
    }

    this.localImagePreviewUrls.set(media.fileId, URL.createObjectURL(file));
  }

  private revokeLocalMediaPreview(fileId: string): void {
    if (!this.isLocalMediaFileId(fileId)) {
      return;
    }

    const existing = this.localImagePreviewUrls.get(fileId);
    if (existing) {
      URL.revokeObjectURL(existing);
      this.localImagePreviewUrls.delete(fileId);
    }
  }

  private cleanupLocalMediaPreviewsForDocument(doc: InternalDocumentItem): void {
    for (const media of doc.media) {
      this.revokeLocalMediaPreview(media.fileId);
    }
    this.cleanupLocalFilePreviews(doc.selectedFiles);
  }

  private cleanupRemovedLocalMedia(previousMedia: DocumentMedia[], currentMedia: DocumentMedia[]): void {
    const currentIds = new Set(currentMedia.map((media) => media.fileId));
    for (const media of previousMedia) {
      if (!currentIds.has(media.fileId)) {
        this.revokeLocalMediaPreview(media.fileId);
      }
    }
  }

  private cleanupRemovedLocalFiles(previousFiles: File[], currentFiles: File[]): void {
    const current = new Set(currentFiles);
    for (const file of previousFiles) {
      if (!current.has(file)) {
        this.revokeLocalFilePreview(file);
      }
    }
  }

  private cleanupDiscardedDetailLocalMedia(): void {
    if (!this.selectedDocument || !this.selectedDocumentDraft) {
      return;
    }

    const persistedIds = new Set(this.selectedDocument.media.map((media) => media.fileId));
    for (const media of this.selectedDocumentDraft.media) {
      if (!persistedIds.has(media.fileId)) {
        this.revokeLocalMediaPreview(media.fileId);
      }
    }

    const persistedFiles = new Set(this.selectedDocument.selectedFiles);
    for (const file of this.selectedDocumentDraft.selectedFiles) {
      if (!persistedFiles.has(file)) {
        this.revokeLocalFilePreview(file);
      }
    }
  }

  private revokeAllLocalMediaPreviews(): void {
    this.localImagePreviewUrls.forEach((previewUrl) => {
      URL.revokeObjectURL(previewUrl);
    });
    this.localImagePreviewUrls.clear();

    this.localFilePreviewUrls.forEach((previewUrl) => {
      URL.revokeObjectURL(previewUrl);
    });
    this.localFilePreviewUrls.clear();
    this.localFileIds.clear();
  }

  private isPotentialFileDrag(event: DragEvent): boolean {
    if (!this.canEdit) {
      return false;
    }

    const dataTransfer = event.dataTransfer;
    if (!dataTransfer) {
      return false;
    }

    const types = Array.from(dataTransfer.types || []);
    if (types.some((type) => type === 'Files' || type.toLowerCase() === 'files')) {
      return true;
    }

    if (Array.from(dataTransfer.items || []).some((item) => item.kind === 'file')) {
      return true;
    }

    if (types.length === 0 && dataTransfer.files?.length > 0) {
      return true;
    }

    // macOS Mail exposes message URIs during drag instead of the standard Files type.
    return types.some((type) => {
      const normalized = type.toLowerCase();
      return normalized === 'text/uri-list'
        || normalized === 'text/plain'
        || normalized.includes('file')
        || normalized.includes('promise');
    });
  }

  private async extractDroppedFiles(event: DragEvent): Promise<{ files: File[]; incompleteMailDrop: boolean }> {
    const dataTransfer = event.dataTransfer;
    if (!dataTransfer) {
      return { files: [], incompleteMailDrop: false };
    }

    const syncStrings = snapshotMailDropStrings(dataTransfer);
    const stringItemReads = startMailDropStringItemReads(dataTransfer.items);
    const entryReads = this.startWebkitEntryReads(dataTransfer.items);
    const handleReads = this.startFileSystemHandleReads(dataTransfer.items);

    const asyncStrings = await stringItemReads;
    const mailPayload = resolveMailDropPayload(mergeMailDropStringMaps(syncStrings, asyncStrings));
    const payloadFiles = buildFilesFromMailDropPayload(mailPayload, this.getMailDropBodyFallback());

    const collected: File[] = [];
    const seen = new Set<string>();
    const addFile = (file: File | null | undefined): void => {
      if (!file) {
        return;
      }

      const key = `${file.name}::${file.size}::${file.lastModified}::${file.type}`;
      if (seen.has(key)) {
        return;
      }

      seen.add(key);
      collected.push(file);
    };

    this.extractFilesFromFileList(dataTransfer.files).forEach(addFile);
    this.extractFilesFromDataTransferItems(dataTransfer.items).forEach(addFile);

    const fromEntries = await Promise.all(entryReads);
    fromEntries.flat().forEach(addFile);

    const fromHandles = await Promise.all(handleReads);
    fromHandles.flat().forEach(addFile);

    let incompleteMailDrop = false;
    let files = this.normalizeDroppedEmailFiles(await this.inferEmailFiles(collected));
    const enrichResult = await this.enrichIncompleteDroppedEmailFiles(files, mailPayload, payloadFiles);
    files = enrichResult.files;
    incompleteMailDrop = enrichResult.incompleteMailDrop;

    const partitionResult = await this.partitionMailDropFallbackFiles(files);
    files = partitionResult.files;
    incompleteMailDrop = incompleteMailDrop || partitionResult.incompleteMailDrop;

    if (files.length > 0) {
      return { files, incompleteMailDrop };
    }

    const incomingFile = await this.tryBuildEmailFileFromIncomingMessage(mailPayload);
    if (incomingFile) {
      return { files: [incomingFile], incompleteMailDrop };
    }

    if (payloadFiles.length > 0) {
      if (mailDropPayloadWouldUseBodyFallback(mailPayload)) {
        return { files: [], incompleteMailDrop: true };
      }

      return { files: payloadFiles, incompleteMailDrop };
    }

    return { files: [], incompleteMailDrop };
  }

  private async enrichIncompleteDroppedEmailFiles(
    files: File[],
    mailPayload: MailDropPayload,
    payloadFiles: File[],
  ): Promise<{ files: File[]; incompleteMailDrop: boolean }> {
    if (files.length === 0) {
      return { files, incompleteMailDrop: false };
    }

    let incompleteMailDrop = false;
    const enriched = await Promise.all(files.map(async (file) => {
      const content = await file.slice(0, Math.min(file.size, 512000)).text();
      const subject = mailPayload.subject || this.extractSubjectFromEml(content);
      if (!isEmlContentIncomplete(content, subject)) {
        return file;
      }

      const incomingFile = await this.tryBuildEmailFileFromIncomingMessage(mailPayload);
      if (incomingFile) {
        return incomingFile;
      }

      if (payloadFiles.length > 0 && mailDropPayloadWouldUseBodyFallback(mailPayload)) {
        incompleteMailDrop = true;
        return null;
      }

      const payloadFile = payloadFiles[0];
      if (payloadFile) {
        return payloadFile;
      }

      return file;
    }));

    return {
      files: enriched.filter((file): file is File => !!file),
      incompleteMailDrop,
    };
  }

  private async partitionMailDropFallbackFiles(
    files: File[],
  ): Promise<{ files: File[]; incompleteMailDrop: boolean }> {
    const checks = await Promise.all(files.map(async (file) => ({
      file,
      isFallback: await fileContainsMailDropFallback(file),
    })));

    return {
      files: checks.filter((check) => !check.isFallback).map((check) => check.file),
      incompleteMailDrop: checks.some((check) => check.isFallback),
    };
  }

  private async tryBuildEmailFileFromIncomingMessage(payload: MailDropPayload): Promise<File | null> {
    if (!payload.messageId) {
      return null;
    }

    const clientId = this.contextService.getCurrentClientId();
    const subsidiaryId = this.contextService.getCurrentSubsidiaryId();
    if (!clientId || !subsidiaryId) {
      return null;
    }

    for (const messageId of buildMessageIdLookupCandidates(payload.messageId)) {
      try {
        const listResponse = await firstValueFrom(
          this.incomingMessagesService.getIncomingMessages(clientId, subsidiaryId, {
            page: 1,
            pageSize: 1,
            filters: [{
              field: 'messageId',
              operator: 'equals',
              value: messageId,
              metaType: 'TEXT',
            }],
          }),
        );

        const match = listResponse.data?.[0];
        if (!match?.id) {
          continue;
        }

        const detailResponse = await firstValueFrom(
          this.incomingMessagesService.getIncomingMessage(clientId, subsidiaryId, match.id),
        );
        const emlContent = buildEmlFromIncomingMessage(detailResponse.data);
        if (isEmlContentIncomplete(emlContent, detailResponse.data.subject || payload.subject)) {
          continue;
        }

        return new File(
          [emlContent],
          buildMailDropFileName(detailResponse.data.subject || payload.subject, messageId),
          { type: 'message/rfc822' },
        );
      } catch {
        continue;
      }
    }

    return null;
  }

  private extractSubjectFromEml(content: string): string {
    const match = content.match(/^Subject:\s*(.+)$/im);
    return match?.[1]?.trim() || '';
  }

  private getMailDropBodyFallback(): string {
    return this.languageService.instant('DOCUMENTS_FIELD.MAIL_DROP_BODY_FALLBACK');
  }

  private async notifyIncompleteMailDrop(): Promise<void> {
    if (!this.authService.shouldShowIncompleteEmailDropMessage()) {
      return;
    }

    const dontShowAgain = await this.miscService.showAlertWithDontShowAgainOption(
      'DOCUMENTS_FIELD.MAIL_DROP_BODY_FALLBACK',
      'DOCUMENTS_FIELD.MAIL_DROP_DO_NOT_SHOW_AGAIN',
    );

    if (!dontShowAgain) {
      return;
    }

    try {
      await firstValueFrom(this.authService.setShowIncompleteEmailDropMessage(false));
      await firstValueFrom(this.authService.refreshAccessToken());
    } catch {
      // Preference save is best-effort.
    }
  }

  private startWebkitEntryReads(items: DataTransferItemList | null | undefined): Array<Promise<File[]>> {
    if (!items) {
      return [];
    }

    const entries = Array.from(items)
      .filter((item) => typeof item.webkitGetAsEntry === 'function')
      .map((item) => item.webkitGetAsEntry())
      .filter((entry): entry is FileSystemEntry => !!entry);

    return entries.map((entry) => this.readFilesFromFileSystemEntry(entry));
  }

  private startFileSystemHandleReads(items: DataTransferItemList | null | undefined): Array<Promise<File[]>> {
    if (!items) {
      return [];
    }

    const reads: Array<Promise<File[]>> = [];
    for (const item of Array.from(items)) {
      const getHandle = (item as DataTransferItem & {
        getAsFileSystemHandle?: () => Promise<FileSystemHandle | null>;
      }).getAsFileSystemHandle;

      if (typeof getHandle !== 'function') {
        continue;
      }

      reads.push(
        getHandle.call(item).then(async (handle) => {
          if (!handle || handle.kind !== 'file') {
            return [];
          }

          return [await (handle as FileSystemFileHandle).getFile()];
        }),
      );
    }

    return reads;
  }

  private async inferEmailFiles(files: File[]): Promise<File[]> {
    return Promise.all(files.map((file) => this.inferEmailFile(file)));
  }

  private async inferEmailFile(file: File): Promise<File> {
    const normalizedName = (file.name || '').toLowerCase();
    const normalizedType = (file.type || '').toLowerCase();

    if (normalizedName.endsWith('.eml')
      || normalizedName.endsWith('.msg')
      || normalizedType.includes('message/rfc822')
      || normalizedType.includes('vnd.ms-outlook')) {
      return file;
    }

    try {
      const header = await file.slice(0, 4096).text();
      if (/^From:\s/m.test(header) || /^MIME-Version:/m.test(header) || /^Received:/m.test(header)) {
        const baseName = file.name.trim() || 'email';
        const fileName = baseName.toLowerCase().endsWith('.eml') ? baseName : `${baseName}.eml`;
        return new File([file], fileName, {
          type: 'message/rfc822',
          lastModified: file.lastModified,
        });
      }
    } catch {
      return file;
    }

    return file;
  }

  private extractFilesFromDataTransferItems(items: DataTransferItemList | null | undefined): File[] {
    if (!items) {
      return [];
    }

    const files: File[] = [];
    for (const item of Array.from(items)) {
      const file = item.getAsFile();
      if (file) {
        files.push(file);
      }
    }

    return files;
  }

  private readFilesFromFileSystemEntry(entry: FileSystemEntry): Promise<File[]> {
    if (entry.isFile) {
      return new Promise((resolve) => {
        (entry as FileSystemFileEntry).file(
          (file) => resolve([file]),
          () => resolve([]),
        );
      });
    }

    if (!entry.isDirectory) {
      return Promise.resolve([]);
    }

    return this.readFilesFromDirectoryEntry(entry as FileSystemDirectoryEntry);
  }

  private readFilesFromDirectoryEntry(directoryEntry: FileSystemDirectoryEntry): Promise<File[]> {
    return new Promise((resolve) => {
      const reader = directoryEntry.createReader();
      const files: File[] = [];

      const readBatch = (): void => {
        reader.readEntries(async (entries) => {
          if (entries.length === 0) {
            resolve(files);
            return;
          }

          const nested = await Promise.all(entries.map((entry) => this.readFilesFromFileSystemEntry(entry)));
          files.push(...nested.flat());
          readBatch();
        }, () => resolve(files));
      };

      readBatch();
    });
  }

  private normalizeDroppedEmailFiles(files: File[]): File[] {
    return files.map((file) => this.normalizeDroppedEmailFile(file));
  }

  private normalizeDroppedEmailFile(file: File): File {
    const normalizedName = (file.name || '').toLowerCase();
    const normalizedType = (file.type || '').toLowerCase();

    if (normalizedName.endsWith('.eml') || normalizedName.endsWith('.msg')) {
      return file;
    }

    if (normalizedType.includes('message/rfc822')) {
      const baseName = file.name.trim() || 'email';
      const fileName = baseName.toLowerCase().endsWith('.eml') ? baseName : `${baseName}.eml`;
      return new File([file], fileName, {
        type: file.type || 'message/rfc822',
        lastModified: file.lastModified,
      });
    }

    if (normalizedType.includes('vnd.ms-outlook')) {
      const baseName = file.name.trim() || 'email';
      const fileName = baseName.toLowerCase().endsWith('.msg') ? baseName : `${baseName}.msg`;
      return new File([file], fileName, {
        type: file.type || 'application/vnd.ms-outlook',
        lastModified: file.lastModified,
      });
    }

    return file;
  }

  private extractFilesFromFileList(fileList: FileList | null): File[] {
    if (!fileList || fileList.length === 0) {
      return [];
    }

    const files: File[] = [];
    for (let index = 0; index < fileList.length; index += 1) {
      const file = fileList.item(index);
      if (file) {
        files.push(file);
      }
    }
    return files;
  }

  private appendFilesToNewDocumentSelection(files: File[]): void {
    for (const file of files) {
      this.registerLocalFilePreview(file);
      this.newDocumentSelectedFiles.push(file);
    }
    this.newDocumentSelectedFiles = [...this.newDocumentSelectedFiles];
  }

  private async handleDroppedFiles(context: DocumentDropContext, files: File[]): Promise<void> {
    if (!this.canEdit || files.length === 0) {
      return;
    }

    if (context.kind === 'document' && context.documentId) {
      this.addFilesToDocument(context.documentId, files);
      return;
    }

    if (context.kind === 'newDocument') {
      this.newDocumentFormInteracted = true;
      this.appendFilesToNewDocumentSelection(files);
      const defaultName = this.getDefaultDocumentNameFromFiles(files);
      if (!this.newDocumentFolderName.trim()) {
        this.newDocumentFolderName = defaultName;
      }
      if (!this.newDocumentName.trim()) {
        this.newDocumentName = defaultName;
      }
      this.cdr.markForCheck();
      return;
    }

    this.openDropCreateModal(context, files);
  }

  private addFilesToDocument(documentId: string, files: File[]): void {
    const document = this.documents.find((item) => item.id === documentId);
    if (!document) {
      return;
    }

    for (const file of files) {
      this.registerLocalFilePreview(file);
    }

    const updatedDocument: InternalDocumentItem = {
      ...document,
      selectedFiles: [...document.selectedFiles, ...files],
      __source: { ...document.__source },
    };
    this.replaceDocument(updatedDocument);
  }

  private openDropCreateModal(context: DocumentDropContext, files: File[]): void {
    for (const file of files) {
      this.registerLocalFilePreview(file);
    }

    this.dropCreateContext = context;
    const defaultNameFromFiles = this.getDefaultDocumentNameFromFiles(files);
    this.dropCreateDraft = {
      folderCategory: context.categoryId || '',
      folderType: context.typeId || '',
      folderName: context.folderName || defaultNameFromFiles,
      documentName: defaultNameFromFiles,
      documentDescription: '',
      documentDate: this.getTodayDateInputValue(),
      appendix: false,
      files: [...files],
    };
    this.isDropCreateModalOpen = true;
    this.initFolderPicker(
      'dropCreate',
      context.categoryId || '',
      context.typeId || '',
      this.getDropCreateLockedCategoryIdForContext(context),
    );
  }

  private getDropCreateLockedCategoryIdForContext(context: DocumentDropContext): string {
    if (context.kind === 'category' || context.kind === 'folderType' || context.kind === 'folderName') {
      return context.categoryId || '';
    }
    return '';
  }

  private getDefaultDocumentNameFromFiles(files: File[]): string {
    const firstFile = files[0];
    if (!firstFile) {
      return '';
    }

    const extensionIndex = firstFile.name.lastIndexOf('.');
    if (extensionIndex <= 0) {
      return firstFile.name;
    }

    return firstFile.name.slice(0, extensionIndex);
  }

  private buildDropTargetKey(context: DocumentDropContext): string {
    switch (context.kind) {
      case 'document':
        return `doc:${context.documentId || ''}`;
      case 'category':
        return `category:${context.categoryId || ''}`;
      case 'folderType':
        return `type:${context.categoryId || ''}::${context.typeId || ''}`;
      case 'folderName':
        return `folder:${context.categoryId || ''}::${context.typeId || ''}::${context.folderName || ''}`;
      case 'newDocument':
        return 'new-document';
      default:
        return '';
    }
  }

  private registerLocalFilePreview(file: File): void {
    if (!this.isImageMedia(file.type || '')) {
      return;
    }
    if (this.localFilePreviewUrls.has(file)) {
      return;
    }
    this.localFilePreviewUrls.set(file, URL.createObjectURL(file));
  }

  private revokeLocalFilePreview(file: File): void {
    const existing = this.localFilePreviewUrls.get(file);
    if (existing) {
      URL.revokeObjectURL(existing);
      this.localFilePreviewUrls.delete(file);
    }
    this.localFileIds.delete(file);
  }

  private cleanupLocalFilePreviews(files: File[]): void {
    for (const file of files) {
      this.revokeLocalFilePreview(file);
    }
  }

  private ensureLocalFilePreviews(files: File[]): void {
    for (const file of files) {
      this.registerLocalFilePreview(file);
      this.getLocalFileId(file);
    }
  }

  private getLocalFileId(file: File): string {
    const existing = this.localFileIds.get(file);
    if (existing) {
      return existing;
    }
    const id = `local_file_${Date.now()}_${Math.floor(Math.random() * 100000)}_${file.name}`;
    this.localFileIds.set(file, id);
    return id;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private readString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private readBoolean(value: unknown): boolean {
    return value === true;
  }
}